import { useState, useEffect,useCallback } from "react";
import {parseJwtToken, getCultureFromLs} from '../../../utils/commonFunctions.js';
import { APIEndpoint,DFPAPIEndpointGalley} from "../../../utils/axios";
import CustomSelect from '../../../common/CustomSelect';
import { saveAs } from 'file-saver';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingGIF from '../../../assets/loadingIn.gif';


const toastError = (msg) => {
  toast.dismiss();
  toast.error(msg, {
    position: "top-center",
    hideProgressBar: true,
    pauseOnHover: true,
    draggable: false,
    autoClose: 3800,
    style: {
      backgroundColor: '#d9534f',
      color: '#fff',
      borderRadius: '8px',
      fontSize: '0.95rem',
      minWidth: '28rem',
    }
  });
};

const toastSuccess = (msg) => {
  toast.dismiss();
  toast.success(msg, {
    position: "top-center",
    hideProgressBar: true,
    pauseOnHover: true,
    draggable: false,
    autoClose: 3000,
    style: {
      backgroundColor: '#4cae4c',
      color: '#fff',
      borderRadius: '8px',
      fontSize: '0.95rem',
      minWidth: '28rem',
    }
  });
};


const VIEW_MODES = [
  { id:"group",  title:"Grouped view",
    icon:<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg> },
  { id:"expand", title:"Expanded view — one row per aircraft",
    icon:<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2.5" rx="1"/><rect x="1" y="6.5" width="14" height="2.5" rx="1"/><rect x="1" y="11" width="14" height="2.5" rx="1"/></svg> },
];


function Btn({disabled = false,children, variant="ghost", onClick, className="" }) {
  return <button onClick={onClick} disabled={disabled}  className={`btn btn-${variant} ${className}`}>{children}</button>;
}

function Notice({ children, icon="ℹ", warn=false }) {
  return (
    <div className={`notice ${warn ? "notice-warn" : ""}`}>
      <span className="notice-icon">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div className="section-divider">
      <div className="section-divider__line" />
      <span className="section-divider__label">{label}</span>
      <div className="section-divider__line" />
    </div>
  );
}

function Toggle({ label, desc, on, onToggle }) {
  return (
    <div className={`toggle-row ${on ? "toggle-row--on" : ""}`} onClick={onToggle}>
      <div className="toggle-info">
        <div className="toggle-label">{label}</div>
        {desc && <div className="toggle-desc">{desc}</div>}
      </div>
      <div className={`toggle-switch ${on ? "toggle-switch--on" : ""}`}>
        <div className={`toggle-switch__knob ${on ? "toggle-switch__knob--on" : ""}`} />
      </div>
    </div>
  );
}

function SimpleToggle({ defaultOn=false }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <div className={`simple-toggle ${on ? "simple-toggle--on" : ""}`} onClick={() => setOn(v => !v)}>
      <div className={`simple-toggle__knob ${on ? "simple-toggle__knob--on" : ""}`} />
    </div>
  );
}

function Chip({ label, selected, onClick }) {
  return (
    <div className={`chip ${selected ? "chip--selected" : ""}`} onClick={onClick}>{label}</div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`status-badge status-badge--${status}`}>
      <span className="status-badge__dot" />
      {status.charAt(0).toUpperCase()+status.slice(1)}
    </span>
  );
}

const OPS_ICONS = {
  both: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.5595 3.90961C21.1495 4.49961 21.1495 5.44961 20.5595 6.02961L16.6695 9.91961L18.7895 19.1096L17.3795 20.5296L13.4995 13.0996L9.59945 16.9996L9.95945 19.4696L8.88945 20.5296L7.12945 17.3496L3.93945 15.5796L4.99945 14.4996L7.49945 14.8696L11.3695 10.9996L3.93945 7.08961L5.35945 5.67961L14.5495 7.79961L18.4395 3.90961C18.9995 3.32961 19.9995 3.32961 20.5595 3.90961Z"/>
    </svg>
  ),
  cateringOnly: (
    <svg width="12" height="12" viewBox="0 -960 960 960" fill="currentColor">
      <path d="M533-440q-32-45-84.5-62.5T340-520q-56 0-108.5 17.5T147-440h386ZM40-360q0-109 91-174.5T340-600q118 0 209 65.5T640-360H40Zm0 160v-80h600v80H40ZM720-40v-80h56l56-560H450l-10-80h200v-160h80v160h200L854-98q-3 25-22 41.5T788-40h-68Zm0-80h56-56ZM80-40q-17 0-28.5-11.5T40-80v-40h600v40q0 17-11.5 28.5T600-40H80Zm260-400Z"/>
    </svg>
  ),
  dutyFreeOnly : (
    <svg width="12" height="12" viewBox="0 -960 960 960" fill="currentColor">
      <path d="M240-80q-33 0-56.5-23.5T160-160v-480q0-33 23.5-56.5T240-720h80q0-66 47-113t113-47q66 0 113 47t47 113h80q33 0 56.5 23.5T800-640v480q0 33-23.5 56.5T720-80H240Zm0-80h480v-480h-80v80q0 17-11.5 28.5T600-520q-17 0-28.5-11.5T560-560v-80H400v80q0 17-11.5 28.5T360-520q-17 0-28.5-11.5T320-560v-80h-80v480Zm160-560h160q0-33-23.5-56.5T480-800q-33 0-56.5 23.5T400-720ZM240-160v-480 480Z"/>
    </svg>
  ),
};

function OpsBadge({ ops, label }) {
  const cls = ops === "both" ? "both" : ops === "cateringOnly" ? "cateringOnly" : "dutyFreeOnly";
  return (
    <span className={`ops-badge ops-badge--${cls}`}>
      {OPS_ICONS[cls]}
      {label}
    </span>
  );
}

function AcChip({ code, ac }) {
  const hasDetail = ac && (ac.body || ac.seats || ac.cls) && ac.body !== "—";

  return (
    <span className="ac-chip-wrap">
      <span className="ac-chip">{code}</span>
      {hasDetail && (
        <div className="ac-chip-tooltip">
          <div className="ac-chip-tooltip__code">{ac.code}</div>
          {ac.body  && <div className="ac-chip-tooltip__row"><span>Body type</span><span>{ac.body}</span></div>}
          {ac.cls   && <div className="ac-chip-tooltip__row"><span>Cabin class</span><span>{ac.cls}</span></div>}
          {ac.seats && <div className="ac-chip-tooltip__row"><span>Seat config</span><span>{ac.seats} seats</span></div>}
        </div>
      )}
    </span>
  );
}


function FormGroup({ label, required, hint, children }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}{required && <span className="required">*</span>}
      </label>
      {children}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   VIEW PAGE Landing**
───────────────────────────────────────────────────────────────────────────── */
function ViewPage({ config, onBack, onEdit, onStdUplift }) {
  const [detail,        setDetail]       = useState(null);
  const [upliftDefault, setUpliftDefault] = useState([]);
  const [upliftLong,    setUpliftLong]   = useState([]);
  const [masterItems,   setMasterItems]  = useState([]);
  const [loading,       setLoading]      = useState(true);
  const galleys     = config._galleys     || [];
  const trolleyRows = config._trolleyRows || [];

  useEffect(() => {
    if (!config?.id) { setLoading(false); return; }
    (async () => {
      try {
        const storedCreds = localStorage.getItem('login_credentials');
        if (!storedCreds) return;
        const { data: creds } = JSON.parse(storedCreds) || {};
        const tokenResp = await parseJwtToken(creds);
        if (!tokenResp?.userId) return;
        const hdrs = { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };

        // 1. Config detail
        const res = await APIEndpoint.get(`catering-group/config/${config.id}`, hdrs);
        if (res?.success && res?.data) setDetail(res.data);

        // 2. Standard uplift records
        try {
          const upliftRes = await APIEndpoint.get(`catering-group/config/${config.id}/standard-uplift`, hdrs);
          if (upliftRes?.success && Array.isArray(upliftRes.items)) {
            upliftRes.items.forEach(doc => {
              if (doc.haulType === "long") setUpliftLong(doc.items || []);
              else setUpliftDefault(doc.items || []);
            });
          }
        } catch (err) {
          if (err?.response?.status !== 404) console.error('ViewPage fetchUplift error:', err);
        }

        // 3. Master items for name lookup
        try {
          const base = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
          const probe = await APIEndpoint.post('master/process', { ...base, limit:10 }, hdrs);
          const total = probe?.count || probe?.items?.length || 10;
          const full  = await APIEndpoint.post('master/process', { ...base, limit:total }, hdrs);
          if (full?.items?.length) setMasterItems(full.items);
        } catch (err) {
          console.error('ViewPage fetchMaster error:', err);
        }

      } catch (err) {
        console.error('ViewPage fetchConfig error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [config?.id]);

  const ReviewBlock = ({ title, onEditClick, children }) => (
    <div className="review-block">
      <div className="review-block__header">
        <span className="review-block__title">{title}</span>
        <button className="review-block__edit" onClick={onEditClick}>✎ Edit</button>
      </div>
      {children}
    </div>
  );

  const KV = ({ label, value, mono }) => (
    <div className="review-kv">
      <div className="review-k">{label}</div>
      <div className={`review-v ${mono ? "review-v--mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );

  function getItemName(itemId) {
    return masterItems.find(m => m.itemId === Number(itemId))?.itemName || `Item ${itemId}`;
  }

  const OPS_LABEL  = { both:"Catering + Duty Free", cateringOnly:"Catering Only", dutyFreeOnly:"Duty Free Only" };
  const PREC_LABEL = { first:"Whichever occurs first", sectors:"Max Sectors", maxSpanHours:"Max Duration" };
  const hasLongHaul = upliftLong.length > 0;
  const TROLLEY_TYPE_LABEL = { full:"Full Cart", half:"Half Cart", smu:"SMU", stowage:"Misc. Stowage", standardContainer:"Standard Container" };
  const uniqueTrolleyTypes = [...new Set(trolleyRows.map(u => TROLLEY_TYPE_LABEL[u.type] || u.type))].join(", ");

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark fade-in page-wrap">
      <button className="back-btn" onClick={onBack}>← Back to Configurations</button>

      <div className="view-config-card">
        <div className="view-config-inner">
          <div>
            <div className="view-config-title">Catering Configuration Overview</div>
            <div className="view-config-meta">
              {config.aircraft.map(a => <AcChip key={a.code} code={a.code} ac={a} />)}
              <OpsBadge ops={detail?.operationType || config.ops} label={OPS_LABEL[detail?.operationType || config.ops]} />
              <StatusBadge status={detail?.status || config.status} />
              <span className="view-config-date">Activated: {config.updated} · Expires: {config.expiry}</span>
            </div>
          </div>
         {!loading ? <div className="view-config-actions">
            <Btn variant="primary" onClick={() => onEdit(config)}>✎ Edit Configuration</Btn>
          </div> : <></>}
        </div>
      </div>

      {loading ? (
        <div style={{ padding:"40px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading configuration details…</div>
      ) : (
        <>
          <ReviewBlock title="Airline & Aircraft" onEditClick={() => onEdit(config)}>
            <div className="review-kv-grid">
              <KV label="Airline Name"    value={getCultureFromLs()?.countryName} />
              <KV label="IATA Code"       value={config.iata} mono />
              <KV label="Operations"      value={OPS_LABEL[detail?.operationType] || config.opsLabel} />
              <KV label="Route Type"      value={detail?.type === "int" ? "International" : "Domestic"} mono />
              <KV label="Aircraft Types"  value={config.aircraft.map(a => a.code).join(", ")} mono />
              <KV label="Status"          value={detail?.status ? detail.status.charAt(0).toUpperCase() + detail.status.slice(1) : "—"} />
              <KV label="Created"         value={detail?.createdAt ? new Date(detail.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—"} />
              <KV label="Last Updated"    value={detail?.updatedAt ? new Date(detail.updatedAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—"} />
            </div>
          </ReviewBlock>

          <ReviewBlock title="Catering Group Rules" onEditClick={() => onEdit(config)}>
            <div className="review-kv-grid">
              <KV label="Max Sectors"          value={detail?.maxSectors != null ? `${detail.maxSectors} sectors` : "—"} mono />
              <KV label="Max Duration"         value={detail?.maxSpanHours != null ? `${detail.maxSpanHours} hrs` : "—"} mono />
              <KV label="Long Haul Threshold"  value={detail?.longHaulThresholdHours != null ? `${detail.longHaulThresholdHours} hrs` : "Not set"} mono />
              <KV label="Precedence Rule"      value={PREC_LABEL[detail?.limitPriority] || "—"} />
              <KV label="Topup Allowed"        value={detail?.allowTopup != null ? (detail.allowTopup ? "Yes" : "No") : "—"} />
              <KV label="IROP Auto Sub-group"  value={detail?.createSubGroupOnIROP != null ? (detail.createSubGroupOnIROP ? "Enabled" : "Disabled") : "—"} />
              <KV label="Sector Cancellation"  value={detail?.sectorCancellationStrategy || "—"} />
            </div>
          </ReviewBlock>

          <ReviewBlock title="Uplift Configuration" onEditClick={() => onStdUplift()}>
            <div className="review-kv-grid">
              <KV label="Calculation Base"      value={detail?.enableTrolleyGalleyConfig ? "Trolley Configuration" : "Standard Uplift"} />
              <KV label="Long Haul Threshold"   value={detail?.longHaulThresholdHours != null ? `${detail.longHaulThresholdHours} hrs` : "Not configured"} mono />
              <KV label="Pre-Book Threshold"    value={detail?.prebookThreshold != null ? `${detail.prebookThreshold} passengers` : "—"} mono />
              <KV label="Sales Commit Threshold" value={detail?.allowedDiscrepancyThreshold != null ? `${detail.allowedDiscrepancyThreshold}% variance` : "—"} mono />
              <KV label="Activation Date"       value={detail?.activationDate ? new Date(detail.activationDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—"} />
              <KV label="Expiry Date"           value={detail?.expiryDate ? new Date(detail.expiryDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—"} />
            </div>
            {upliftDefault.length > 0 && (
              <div className="mb-16 mt-8">
                <div className="font-xs text-muted mb-8" style={{ textTransform:"uppercase", letterSpacing:"0.04em", padding:"0 18px", fontSize:11, color:"#3d5a7a" }}>Items</div>
                <table className="data-table catering-table-main">
                  <thead>
                    <tr>
                      {["Item", "Standard Qty", ...(hasLongHaul ? ["Long Haul Qty"] : [])].map(h => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {upliftDefault.map((item, i) => {
                      const longItem = upliftLong.find(l => l.itemId === item.itemId);
                      return (
                        <tr key={i}>
                          <td>{getItemName(item.itemId)}</td>
                          <td>{item.quantity}</td>
                          {hasLongHaul && (
                            <td style={{ color: longItem ? "#4cc3e6" : "#3d5a7a" }}>
                              {longItem ? longItem.quantity : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ReviewBlock>

          {/* ── Galley & Trolley Review Block ── */}
          <ReviewBlock title="Galley & Trolley" onEditClick={() => onStdUplift()}>
            <div className="review-kv-grid">
              <KV label="Self-Assigned Trolleys" value={detail?.allowTrolleySelfAssignByCrew != null ? (detail.allowTrolleySelfAssignByCrew ? "Enabled" : "Disabled") : "—"} />
              <KV label="Seal Number Tracking"   value={detail?.useSealNumberTracking != null ? (detail.useSealNumberTracking ? "Enabled" : "Disabled") : "—"} />
              <KV label="Galleys"                value={galleys.length ? galleys.map(g => g.name).join(", ") : "—"} mono />
              <KV label="Trolley Types"          value={uniqueTrolleyTypes || "—"} />
            </div>
            {trolleyRows.length > 0 && (
              <div className="mb-16 mt-8">
                <div style={{ textTransform:"uppercase", letterSpacing:"0.04em", padding:"0 18px", fontSize:11, color:"#3d5a7a", marginBottom:8 }}>Trolley Configuration</div>
                <table className="data-table catering-table-main">
                  <thead>
                    <tr>{["Trolley","Type","Category","Total Qty","Galley"].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {trolleyRows.map((u, i) => {
                      const sentinel = (u.items || []).find(it => String(it.itemId) === "totalQty");
                      const totalQty = sentinel ? sentinel.maxQuantity : (u.items || []).reduce((s, it) => s + (Number(it.maxQuantity) || 0), 0) || "—";
                      return (
                        <tr key={u._id || i}>
                          <td style={{ fontFamily:"monospace", fontSize:13 }}>{u.name}</td>
                          <td>{TROLLEY_TYPE_LABEL[u.type] || u.type}</td>
                          <td>{u.itemCategory ? <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600, border:"1px solid rgba(76,195,230,0.4)", color:"#4cc3e6" }}>{u.itemCategory}</span> : "—"}</td>
                          <td style={{ color:"#4cc3e6", fontFamily:"monospace" }}>{totalQty}</td>
                          <td style={{ color:"#8aaacf" }}>{u.galleyName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ReviewBlock>

        </>
      )}

    {!loading ? <div className="view-footer">
        <Btn variant="ghost" onClick={onBack}>← Back to Configurations</Btn>
        <Btn variant="primary" onClick={() => onEdit(config)}>✎ Edit Configuration</Btn>
      </div> : <></>}
    </div>
  );
}

/* 
   DEACTIVATE CONFIRM MODAL
───────────────────────────────────────────────────────────────────────────── */
function DeactivateModal({ open, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ width: 440, animation:"none", transition:"none" }}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ display:"flex", alignItems:"center", gap:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke=" #e05c5c" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Delete Configuration
            </div>
            <div className="modal-subtitle">This action cannot be undone</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{
            padding: "14px 16px",
            borderRadius: 7,
            background: "rgba(224,92,92,0.08)",
            border: "1px solid rgba(224,92,92,0.3)",
            color: "#8aaacf",
            fontSize: 13.5,
            lineHeight: 1.65,
            marginBottom: 16,
          }}>
            This configuration will be <strong style={{ color:" #e05c5c" }}>soft-deleted</strong> and removed from active listings.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   LISTING PAGE
───────────────────────────────────────────────────────────────────────────── */
function ListingPage({ configs: initialConfigs, newConfigId, onNewConfig, onView, onEdit, onStdUplift }) {
  const [search, setSearch]             = useState("");
  const [filter, setFilter]             = useState("all");
  const [viewMode, setViewMode]         = useState("group");
  const [configs, setConfigs]           = useState(initialConfigs);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteUpliftTarget, setDeleteUpliftTarget] = useState(null); // { id: configId }
  const [deletingUplift, setDeletingUplift] = useState(false);
  const [acMetaMap, setAcMetaMap]       = useState({});
  const [viewingRowId, setViewingRowId] = useState(null);

  useEffect(() => {
    init();
    const close = () => setOpenDropdown(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function init() {
    const map = await fetchAircraftMeta();
    await fetchConfig(map);
  }

  async function getHeaders() {
    const storedCreds = localStorage.getItem('login_credentials');
    if (!storedCreds) return null;
    const parsedCreds = JSON.parse(storedCreds);
    const { data, headers } = parsedCreds || {};
    const tokenResp = await parseJwtToken(data);
    if (!tokenResp?.userId || !data?.token || !headers?.['x-csrf-token']) return null;
    return { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };
  }

  async function fetchAircraftMeta() {
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return {};
      const base = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
      const probe = await APIEndpoint.post('aircraftTypes/process', { ...base, limit:10 }, hdrs);
      const total = probe?.count || probe?.items?.length || 10;
      const full  = await APIEndpoint.post('aircraftTypes/process', { ...base, limit:total }, hdrs);
      if (full?.items?.length) {
        const map = {};
        full.items.forEach(item => { map[item.id] = {
          model:  item.model,
          label:  item.key,
          body:   item.bodyType || "—",
          seats:  item.capacity || null,
          cls:    item.classCapacity?.length
                    ? item.classCapacity.map(c => `${c.classCode}: ${c.seatCapacity}`).join(", ")
                    : (item.variant || "—"),
        }; });
        setAcMetaMap(map);
        return map;
      }
    } catch (err) {
      console.error('fetchAircraftMeta error:', err);
    }
    return {};
  }

  async function fetchConfig(metaMap = acMetaMap) {
console.log(metaMap)
    try {
      setLoading(true);
      const hdrs = await getHeaders();
      if (!hdrs) return;
      const response = await APIEndpoint.get('catering-group/config', {
        ...hdrs,
        params: { isActive: filter === 'inactive' ? false : true, type: 'dom' }
      });
      if (response?.items?.length) {
        const OPS_LABEL_MAP = {
          cateringOnly: "Catering Only",
          dutyFreeOnly: "Duty Free Only",
          both: "Catering + Duty Free"
        };
        setConfigs(response.items.map(item => ({
          id:       item.id,
          iata:     getCultureFromLs()?.countryCode,
          aircraft: (item.aircraftTypeIds || []).map(id => ({
            id,
            code:  metaMap[id]?.model  || id.split('-')[0],
            body:  metaMap[id]?.body   || "—",
            cls:   metaMap[id]?.cls    || "—",
            seats: metaMap[id]?.seats  || null,
          })),
          _aircraftTypeIds: item.aircraftTypeIds || [],
          ops:      item.operationType || "both",
          opsLabel: OPS_LABEL_MAP[item.operationType] || null,
          status:   item.isActive ? (item.status || "active") : "inactive",
          updated:  item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
                      : "—",
          expiry:   item.expiryDate
                      ? new Date(item.expiryDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
                      : "—",
          activationDate: item.activationDate
                      ? new Date(item.activationDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
                      : "—",
          isNew:    newConfigId ? item.id === newConfigId : false,
          groupRules: {
            maxSectors:   item.maxSectors,
            maxDuration:  `${item.maxSpanHours} hrs`,
            precedence:   item.limitPriority === "first"   ? "Whichever occurs first"
                        : item.limitPriority === "sectors" ? "Max Sectors"
                        :                                    "Max Duration",
            topup:        item.allowTopup ? "Yes" : "No",
            type:         item.type,
            iropAuto: "—", leadNotify: "—", sectorCancel: "—",
          },
          upliftConfig: {
            calcBase:         item.enableTrolleyGalleyConfig ? "Trolley Configuration" : "Standard Uplift",
            lhThreshold:      item.longHaulThresholdHours != null ? `${item.longHaulThresholdHours} hrs` : "—",
            prebookThreshold: item.prebookThreshold != null ? `${item.prebookThreshold} passengers` : "—",
            salesCommit:      "—",
            activationDate:   item.activationDate
                                ? new Date(item.activationDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
                                : "—",
            expiryDate:       item.expiryDate
                                ? new Date(item.expiryDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})
                                : "—",
          },
          upliftItems:[], galleyConfig:{ selfAssign:"—", sealTracking:"—", galleys:"—", trolleyTypes:"—" }, trolleys:[],
          timezone:"—", category:"—", catWarehouses:[], dfWarehouses:[],
        })));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const filtered = configs.filter(c => {
    const matchFilter = filter === "all" || c.status === filter;
    const q = search.toLowerCase();
    return matchFilter && (!q || c.iata.toLowerCase().includes(q) || c.aircraft.some(a => a.code.toLowerCase().includes(q)));
  });

  const rows = viewMode === "expand"
    ? filtered.flatMap(cfg => cfg.aircraft.map(ac => ({ ...cfg, _singleAc: ac })))
    : filtered;

  async function handleView(row) {
    setViewingRowId(row.id);
    try {
      const hdrs = await getHeaders();
      if (!hdrs) { onView(row); return; }
      const aircraftTypeId = row._aircraftTypeIds?.[0] || row.aircraft?.[0]?.id;
      if (!aircraftTypeId) { onView(row); return; }
      const base = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
      const acRes = await APIEndpoint.post('aircraftTypes/process', { ...base, limit:50 }, hdrs);
      if (!acRes?.items?.length) { onView(row); return; }
      const match = acRes.items.find(ac => ac.id === aircraftTypeId);
      if (!match?.galleys?.length) { onView({ ...row, _galleys: [], _trolleyRows: [] }); return; }
      const allTrolleys = [];
      await Promise.all(match.galleys.map(async g => {
        try {
          const tuRes = await APIEndpoint.get(`aircraftTypes/${aircraftTypeId}/galleys/${g._id}/trolleyUnits`, hdrs);
          if (tuRes?.success && tuRes?.data?.trolleyUnits?.length) {
            tuRes.data.trolleyUnits.forEach(u => allTrolleys.push({ ...u, galleyName: g.name }));
          }
        } catch (err) { console.error(`handleView fetchTrolleyUnits error galley ${g._id}:`, err); }
      }));
      onView({ ...row, _galleys: match.galleys, _trolleyRows: allTrolleys });
    } catch (err) {
      console.error('handleView error:', err);
      onView(row);
    }finally {
      setViewingRowId(null);
    }
  }

  async function handleDelete(id) {
    try {
      setDeleting(id);
      const storedCreds = localStorage.getItem('login_credentials');
      if (!storedCreds) return;
      const parsedCreds = JSON.parse(storedCreds);
      const { data, headers } = parsedCreds || {};
      const tokenResp = await parseJwtToken(data);
      if (!tokenResp?.userId || !data?.token || !headers?.['x-csrf-token']) return;
      const resp = await APIEndpoint.delete(`catering-group/config/${id}`, {
        user_id: tokenResp.userId,
        DashboardAccess: tokenResp.userType,
      });
      if(resp && resp?.success){
        toastSuccess(resp?.message || 'Configuration deactivated successfully.');
        setConfigs(prev => prev.filter(c => c.id !== id));
        setDeactivateTarget(null);
      }
    } catch (err) {
      console.error('delete error:', err);
      setDeactivateTarget(null);
      const errorObj = JSON.parse(err.message);
      toastError(errorObj?.data?.message || 'Failed to deactivate configuration. Please try again.');
    } finally {
      setDeleting(null);
    }
  }

  async function handleDeleteUplift(configId) {
    try {
      setDeletingUplift(true);
      const hdrs = await getHeaders();
      if (!hdrs) return;
      const resp = await APIEndpoint.delete(
        `catering-group/config/${configId}/standard-uplift`,
        hdrs
      );
      if (resp?.success) {
        toastSuccess(resp?.message || 'Standard uplift deleted successfully.');
      }
    } catch (err) {
      console.error('deleteUplift error:', err);
      const errorObj = JSON.parse(err.message);
      toastError(errorObj?.data?.message || 'Failed to delete standard uplift.');
    } finally {
      setDeletingUplift(false);
      setDeleteUpliftTarget(null);
    }
  }

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark page-wrap">
      <DeactivateModal
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => handleDelete(deactivateTarget)}
        loading={deleting === deactivateTarget}
      />
      {/* ── Delete Std Uplift confirm modal newe ── */}
      {deleteUpliftTarget && (
        <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && setDeleteUpliftTarget(null)}>
          <div className="modal-box" style={{ width:440, animation:"none", transition:"none" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke=" #e05c5c" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Delete Standard Uplift
                </div>
                <div className="modal-subtitle">This will soft-delete the uplift record</div>
              </div>
              <button className="modal-close" onClick={() => setDeleteUpliftTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ padding:"14px 16px", borderRadius:7, background:"rgba(224,92,92,0.08)", border:"1px solid rgba(224,92,92,0.3)", color:"#8aaacf", fontSize:13.5, lineHeight:1.65 }}>
                The standard uplift items for this configuration will be <strong style={{ color:" #e05c5c" }}>removed</strong>. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteUpliftTarget(null)} disabled={deletingUplift}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDeleteUplift(deleteUpliftTarget)} disabled={deletingUplift}>
                {deletingUplift ? "Deleting..." : "Delete Uplift"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="listing-header">
        <Btn variant="primary" onClick={onNewConfig}>+ Configuration</Btn>
      </div>

      <div className="listing-toolbar">
        <div className="listing-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3d5a7a" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by aircraft type or IATA code…" />
        </div>
        <div className="filter-chips">
          {["all","active","draft","inactive","expired"].map(f => (
            <button key={f} className={`filter-chip ${filter === f ? "filter-chip--active" : ""}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>
        <div className="count-label">
          {viewMode === "expand"
            ? `${rows.length} aircraft${rows.length !== 1 ? "s" : ""}`
            : `${filtered.length} configuration${filtered.length !== 1 ? "s" : ""}`}
        </div>
        <div className="view-toggle">
          {VIEW_MODES.map(({ id, title, icon }) => (
            <button key={id} title={title} className={`view-toggle-btn ${viewMode === id ? "view-toggle-btn--active" : ""}`} onClick={() => setViewMode(id)}>
              {icon}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap" style={{ overflow:"visible" }}>
        <table className="data-table catering-table-main">
           <colgroup>
            <col style={{ width:"30%" }} />
            <col style={{ width:"18%" }} />
            <col style={{ width:"12%" }} />
            <col style={{ width:"14%" }} />
            <col style={{ width:"14%" }} />
            <col style={{ width:"12%" }} />
          </colgroup>
          <thead>
            <tr>{["Aircraft Types","Operations","Status","Activation Date","Expiry Date","Actions"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding:40, textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading configurations…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding:40, textAlign:"center", color:"#3d5a7a", fontSize:13 }}>No configurations found.</td></tr>
            ) : rows.map((row, ri) => (
              <tr key={`${row.id}-${viewMode === "expand" ? row._singleAc.code : "g"}`}>
                <td>
                  {viewMode === "expand" ? <AcChip code={row._singleAc.code} ac={row._singleAc} /> : (
                    <div className="flex flex-wrap gap-4">
                      {row.aircraft.slice(0,3).map(a => <AcChip key={a.code} code={a.code} ac={a} />)}
                      {row.aircraft.length > 3 && <AcChip code={`+${row.aircraft.length-3}`} />}
                    </div>
                  )}
                </td>
                <td><OpsBadge ops={row.ops} label={row.opsLabel} /></td>
                <td><StatusBadge status={row.status} /></td>
                <td className="td-secondary">{row.activationDate || row.updated}</td>
                <td className={row.status === "inactive" ? "td-danger" : "td-secondary"}>
                  {row.expiry !== "—" ? row.expiry : row.upliftConfig?.expiryDate || "—"}
                  </td>
                <td>
                  <div className="flex gap-6 items-center">
                    {row.isNew ? (
                      <button className="btn-new-uplift" onClick={() => onStdUplift(row)}>Add Std Uplift</button>
                    ) : (
                      <>
                        <button className="btn-view" onClick={() => handleView(row)} disabled={viewingRowId === row.id}>{viewingRowId === row.id ? <img
                                      src={LoadingGIF}
                                      className="margBottom0 icon-size-min5"
                                      alt="Loading"
                                    /> : "View"}</button>
                        <div className="dropdown-wrap" onClick={e => e.stopPropagation()}>
                          <button
                            className="dropdown-trigger"
                            disabled={deleting === row.id}
                            onClick={e => { e.stopPropagation(); setOpenDropdown(openDropdown === ri ? null : ri); }}
                          >
                            {deleting === row.id ? "…" : "···"}
                          </button>
                          {openDropdown === ri && (
                            <div className="dropdown-menu-catering">
                              <div className="dropdown-item-catering" onClick={() => { onEdit(row); setOpenDropdown(null); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 1-2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                Edit Configuration
                              </div>
                              <div className="dropdown-item-catering" onClick={() => { onStdUplift(row); setOpenDropdown(null); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                                Edit Std Uplift
                              </div>
                              <div
                                className="dropdown-item-catering"
                                style={{ color:" #e05c5c" }}
                                onClick={() => { setDeactivateTarget(row.id); setOpenDropdown(null); }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                Delete
                              </div>
                              {/* <div
                                className="dropdown-item-catering"
                                style={{ color:" #e05c5c" }}
                                onClick={() => { setDeleteUpliftTarget(row.id); setOpenDropdown(null); }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M10 11v4"/><path d="M14 11v4"/></svg>
                                Delete Std Uplift
                              </div> */}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   CLONE SECTION  button options
───────────────────────────────────────────────────────────────────────────── */
const CLONE_OPTIONS = [
  { id:"cateringGroupRules", label:"Catering Group Rules",    desc:"Max sectors, duration, precedence, IROP handling" },
  { id:"upliftQuantities",   label:"Uplift Quantities",       desc:"Standard uplift items and quantities" },
  { id:"longHaulConfig",     label:"Long Haul Config",        desc:"Long haul threshold and long haul uplift quantities" },
  { id:"galleyLayout",       label:"Galley & Trolley Layout", desc:"Trolley galley config, self-assign and seal tracking settings" },
];

function CloneSection({ aircraftTypes, selectedAircraft, data, setData }) {
  const [cloneFrom,    setCloneFrom]    = useState("");
  const [cloneTo,      setCloneTo]      = useState("");
  const [cloneOptions, setCloneOptions] = useState([]);
  const [fetching,     setFetching]     = useState(false);
  const [cloning,      setCloning]      = useState(false);
  const [sourceConfig, setSourceConfig] = useState(null); // result of GET config/:id

  async function getHeaders() {
    const storedCreds = localStorage.getItem('login_credentials');
    if (!storedCreds) return null;
    const { data: creds } = JSON.parse(storedCreds) || {};
    const tokenResp = await parseJwtToken(creds);
    if (!tokenResp?.userId) return null;
    return { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };
  }


  async function handleCloneFromChange(aircraftTypeId) {
    setCloneFrom(aircraftTypeId);
    setSourceConfig(null);
    if (!aircraftTypeId) return;
    try {
      setFetching(true);
      const hdrs = await getHeaders();
      if (!hdrs) return;

      const res = await APIEndpoint.get('catering-group/config', hdrs);
      if (res?.items?.length) {
        const match = res.items.find(item =>
          (item.aircraftTypeIds || []).includes(aircraftTypeId)
        );
        if (match) {

          const detail = await APIEndpoint.get(`catering-group/config/${match.id}`, hdrs);
          if (detail?.success && detail?.data) {
            setSourceConfig(detail.data);
          }
        }
      }
    } catch (err) {
      console.error('clone source fetch error:', err);
    } finally {
      setFetching(false);
    }
  }

  function toggleOption(id) {
    setCloneOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleClone() {
    if (!cloneFrom || !cloneTo || !cloneOptions.length || !sourceConfig) return;
    setCloning(true);
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;

      const body = {
        aircraftTypeIds: [cloneTo],
        type:            sourceConfig.type            || "dom",
        operationType:   sourceConfig.operationType   || "both",
        status:          "draft",
      };

      if (cloneOptions.includes("cateringGroupRules")) {
        body.maxSectors                 = sourceConfig.maxSectors;
        body.maxSpanHours               = sourceConfig.maxSpanHours;
        body.limitPriority              = sourceConfig.limitPriority;
        body.allowTopup                 = sourceConfig.allowTopup;
        body.createSubGroupOnIROP       = sourceConfig.createSubGroupOnIROP;
        body.sectorCancellationStrategy = sourceConfig.sectorCancellationStrategy;
      }

      if (cloneOptions.includes("longHaulConfig") || cloneOptions.includes("upliftQuantities")) {
        body.longHaulThresholdHours     = sourceConfig.longHaulThresholdHours;
        body.enableStandardUplift       = sourceConfig.enableStandardUplift;
      }

      body.enableTrolleyGalleyConfig    = sourceConfig.enableTrolleyGalleyConfig;
      body.allowTrolleySelfAssignByCrew = sourceConfig.allowTrolleySelfAssignByCrew;
      body.useSealNumberTracking        = sourceConfig.useSealNumberTracking;


      const createRes = await APIEndpoint.post('catering-group/config', body, hdrs);

      if (createRes?.success) {
        const newConfigId = createRes?.data?.id || createRes?.data?.data?.id;

        if (cloneOptions.includes("upliftQuantities") && newConfigId) {
          try {
            const upliftRes = await APIEndpoint.get(
              `catering-group/config/${sourceConfig.id}/standard-uplift`, hdrs
            );
            if (upliftRes?.success && Array.isArray(upliftRes.items)) {
              for (const doc of upliftRes.items) {
  
                if (doc.haulType === "long" && !cloneOptions.includes("longHaulConfig")) continue;
                await APIEndpoint.post(
                  `catering-group/config/${newConfigId}/standard-uplift`,
                  { haulType: doc.haulType, items: doc.items },
                  hdrs
                );
              }
            }
          } catch (err) {
            console.error('clone uplift error:', err);
          }
        }


        setData(d => ({
          ...d,
          aircraftRules: {
            ...(d.aircraftRules || {}),
            [cloneTo]: {
              maxSectors:   sourceConfig.maxSectors   ?? 4,
              maxSpanHours: sourceConfig.maxSpanHours ?? 12,
              allowTopup:   sourceConfig.allowTopup   ?? true,
              routeType:    sourceConfig.type         || "dom",
            },
          },
          ...(cloneOptions.includes("cateringGroupRules") ? {
            limitPriority:              sourceConfig.limitPriority,
            createSubGroupOnIROP:       sourceConfig.createSubGroupOnIROP,
            sectorCancellationStrategy: sourceConfig.sectorCancellationStrategy,
          } : {}),
        }));

        toastSuccess(`Configuration cloned to ${aircraftTypes.find(a => a.id === cloneTo)?.code || cloneTo} successfully.`);

        setCloneFrom(""); setCloneTo(""); setCloneOptions([]); setSourceConfig(null);
      }
    } catch (err) {
      console.error('clone error:', err);
      const errorObj = JSON.parse(err.message);
      toastError(errorObj?.data?.message || 'Clone failed. Please try again.');
    } finally {
      setCloning(false);
    }
  }

  const canClone = cloneFrom && cloneTo && cloneTo !== cloneFrom && cloneOptions.length > 0 && sourceConfig && !cloning;

  return (
    <div style={{ marginBottom:16 }}>

      <div className="form-grid-2" style={{ marginBottom:16 }}>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>
            Copy Settings From <span style={{ color:" #e05c5c" }}>*</span>
          </div>
          <select
            value={cloneFrom}
            onChange={e => handleCloneFromChange(e.target.value)}
            style={{ width:"100%" }}
          >
            <option value="">— Select source —</option>
            {aircraftTypes.map(ac => <option key={ac.id} value={ac.id}>{ac.code}</option>)}
          </select>
          {fetching && <div style={{ fontSize:12, color:"#3d5a7a", marginTop:4 }}>Fetching config…</div>}
          {cloneFrom && !fetching && !sourceConfig && (
            <div style={{ fontSize:12, color:" #e05c5c", marginTop:4 }}>No existing configuration found for this aircraft.</div>
          )}
          {sourceConfig && !fetching && (
            <div style={{ fontSize:12, color:"#4cae4c", marginTop:5 }}>
              ✓ configuration available for cloning.
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>
            Apply Cloned Settings To <span style={{ color:" #e05c5c" }}>*</span>
          </div>
          <select
            value={cloneTo}
            onChange={e => setCloneTo(e.target.value)}
            style={{ width:"100%" }}
          >
            <option value="">— Select target —</option>
            {selectedAircraft
              .filter(id => id !== cloneFrom)
              .map(id => {
                const ac = aircraftTypes.find(a => a.id === id);
                return ac ? <option key={id} value={id}>{ac.code}</option> : null;
              })}
          </select>
        </div>
      </div>

      <div className="chip-row" style={{ alignItems:"center" }}>
        {CLONE_OPTIONS.map(opt => (
          <div
            key={opt.id}
            className={`chip ${cloneOptions.includes(opt.id) ? "chip--selected" : ""}`}
            onClick={() => toggleOption(opt.id)}
            title={opt.desc}
          >
            {opt.label}
          </div>
        ))}
        <button
          className="btn btn-ghost"
          style={{ padding:"7px 20px", marginLeft:4, whiteSpace:"nowrap" }}
          disabled={!canClone}
          onClick={handleClone}
        >
          {cloning ? "Cloning…" : "Clone →"}
        </button>
      </div>
    </div>
  );
}

function Step1({ data, setData }) {
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [acLoading, setAcLoading]         = useState(false);
  const [acError, setAcError]             = useState(null);
  const [warehouses, setWarehouses]       = useState([]);
  const [whLoading, setWhLoading]         = useState(false);
  const [whError, setWhError]             = useState(null);
  const [selected, setSelected]           = useState("cateringOnly"); 

  async function getTokenResp() {
    const storedCreds = localStorage.getItem('login_credentials');
    if (!storedCreds) return null;
    const { data: creds } = JSON.parse(storedCreds) || {};
    const tokenResp = await parseJwtToken(creds);
    return tokenResp?.userId ? tokenResp : null;
  }

// eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAircraftTypes = useCallback(async () => {
    try {
      setAcLoading(true);
      setAcError(null);
      const tokenResp = await getTokenResp();
      if (!tokenResp) return;
      const basePayload = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
      const headers = { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };
      const probe = await APIEndpoint.post('aircraftTypes/process', { ...basePayload, limit:10 }, headers);
      const totalCount = probe?.count || probe?.items?.length || 10;
      const full = await APIEndpoint.post('aircraftTypes/process', { ...basePayload, limit:totalCount }, headers);
      if (full?.items?.length) {
        const mapped = full.items.map(item => ({
          id: item.id, code: item.key || item.model, variant: item.variant, body: item.bodyType, seats: item.capacity,
          classes: item.classCapacity?.length ? item.classCapacity.map(c => `${c.classCode}: ${c.seatCapacity}`).join(", ") : null,
        }));
        setAircraftTypes(mapped);
        setData(d => ({ ...d, aircraftTypesList: mapped }));
      }
    } catch (err) {
      console.error('fetchAircraftTypes error:', err);
      setAcError('Failed to load aircraft types.');
    } finally {
      setAcLoading(false);
    }
  }, [setData]);

  const fetchWarehouses = useCallback(async () => {
    try {
      setWhLoading(true);
      setWhError(null);
      const tokenResp = await getTokenResp();
      if (!tokenResp) return;
      const basePayload = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
      const headers = { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };
      const probe = await APIEndpoint.post('warehouses/process', { ...basePayload, limit:10 }, headers);
      const totalCount = probe?.count || probe?.items?.length || 10;
      const full = await APIEndpoint.post('warehouses/process', { ...basePayload, limit:totalCount }, headers);
      if (full?.items?.length) {
        setWarehouses(full.items.map(item => ({
          code: item.warehouseCode, name: item.warehouseName,
          state: item.stateName || item.stateCode || "", country: item.country || "",
          clusterId: item.clusterId, clusterName: item.cluster?.clusterName || "",
        })));
      }
    } catch (err) {
      console.error('fetchWarehouses error:', err);
      setWhError('Failed to load warehouses.');
    } finally {
      setWhLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAircraftTypes();
    fetchWarehouses();
  }, [fetchAircraftTypes, fetchWarehouses]);

  const toggleAircraft = id => {
    const ac = aircraftTypes.find(a => a.id === id);
    setData(d => {
      const already  = d.selectedAircraft.includes(id);
      const nextIds  = already ? d.selectedAircraft.filter(x => x !== id) : [...d.selectedAircraft, id];
      const nextMeta = { ...(d.aircraftMeta || {}) };
      if (!already && ac) nextMeta[id] = ac.code;
      else if (already)   delete nextMeta[id];
      return { ...d, selectedAircraft: nextIds, aircraftMeta: nextMeta };
    });
  };

  function StationList({ items, loading, error, onRetry }) {
    if (loading) return <div style={{ padding:"14px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading warehouses…</div>;
    if (error) return (
      <div style={{ padding:"10px 14px", borderRadius:6, border:"1px solid rgba(224,92,92,0.3)", background:"rgba(224,92,92,0.06)", color:" #e05c5c", fontSize:13, marginBottom:8 }}>
        {error}&nbsp;
        <button onClick={onRetry} style={{ background:"none", border:"none", color:"#4cc3e6", cursor:"pointer", fontSize:13, textDecoration:"underline" }}>Retry</button>
      </div>
    );
    if (!items.length) return <div style={{ padding:"12px 14px", color:"#3d5a7a", fontSize:13 }}>No warehouses found.</div>;
    return (
      <div style={{ maxHeight:240, overflowY:"auto", border:"1px solid rgba(40,80,140,0.3)", borderRadius:8, marginBottom:12 }}>
        <div className="station-list">
          {items.map(wh => (
            <div key={wh.code} className="station-row">
              <div className="station-code">{wh.code}</div>
              <div className="station-name">
                {wh.name}
                {wh.clusterName && <span style={{ fontSize:11, color:"#3d5a7a", marginLeft:8 }}>{wh.clusterName}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const STATION_TABS = [
    { id:"cateringOnly", label:"Catering Stations",   hint:"Uplift & retrieval for F&B",       desc:"Airports where catering uplift and retrieval operations occur." },
    { id:"dutyFreeOnly", label:"Duty Free Warehouses", hint:"Uplift & retrieval for duty free", desc:"Airports where duty free inventory is uplifted and retrieved." },
  ];
  const activeTab = STATION_TABS.find(t => t.id === selected);

  return (
    <div>
      <Notice>Configuration is tied to aircraft type. All catering group rules, uplift quantities, and galley layouts are defined per aircraft type.</Notice>

      <SectionDivider label="Airline Details" />
      <div className="airline-details">
        {[{k:"Airline Name",v:getCultureFromLs()?.countryName},{k:"IATA Code",v:getCultureFromLs()?.countryCode,mono:true},{k:"Timezone",v:getCultureFromLs()?.timezone?.formattedName},{k:"Category",v:"FSC",chip:true}].map(({ k, v, mono, chip }) => (
          <div key={k}>
            <div className="airline-detail__key">{k}</div>
            {chip ? <AcChip code={v} /> : <div className={`airline-detail__val ${mono ? "airline-detail__val--mono" : ""}`}>{v}</div>}
          </div>
        ))}
      </div>
      <div className="divider-line" />

      <SectionDivider label="Operations Type" />
      <div className='instructions'>
        <span>Select what inflight services this airline operates. This determines how the configuration flow is structured.</span>
      </div>
      <div className="ops-grid">
        {[
          {id:"cateringOnly", icon:(<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M533-440q-32-45-84.5-62.5T340-520q-56 0-108.5 17.5T147-440h386ZM40-360q0-109 91-174.5T340-600q118 0 209 65.5T640-360H40Zm0 160v-80h600v80H40ZM720-40v-80h56l56-560H450l-10-80h200v-160h80v160h200L854-98q-3 25-22 41.5T788-40h-68Zm0-80h56-56ZM80-40q-17 0-28.5-11.5T40-80v-40h600v40q0 17-11.5 28.5T600-40H80Zm260-400Z"/></svg>), label:"Catering Only",  desc:"Inflight food & beverage service only"},
          {id:"dutyFreeOnly", icon:(<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M240-80q-33 0-56.5-23.5T160-160v-480q0-33 23.5-56.5T240-720h80q0-66 47-113t113-47q66 0 113 47t47 113h80q33 0 56.5 23.5T800-640v480q0 33-23.5 56.5T720-80H240Zm0-80h480v-480h-80v80q0 17-11.5 28.5T600-520q-17 0-28.5-11.5T560-560v-80H400v80q0 17-11.5 28.5T360-520q-17 0-28.5-11.5T320-560v-80h-80v480Zm160-560h160q0-33-23.5-56.5T480-800q-33 0-56.5 23.5T400-720ZM240-160v-480 480Z"/></svg>), label:"Duty Free Only", desc:"Inflight retail & duty free sales only"},
          {id:"both",         icon:(<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5595 3.90961C21.1495 4.49961 21.1495 5.44961 20.5595 6.02961L16.6695 9.91961L18.7895 19.1096L17.3795 20.5296L13.4995 13.0996L9.59945 16.9996L9.95945 19.4696L8.88945 20.5296L7.12945 17.3496L3.93945 15.5796L4.99945 14.4996L7.49945 14.8696L11.3695 10.9996L3.93945 7.08961L5.35945 5.67961L14.5495 7.79961L18.4395 3.90961C18.9995 3.32961 19.9995 3.32961 20.5595 3.90961Z"/></svg>), label:"Both",           desc:"Catering and duty free operations"},
        ].map(op => (
          <div key={op.id} className={`ops-card ${data.opsType === op.id ? "ops-card--selected" : ""}`} onClick={() => setData(d => ({ ...d, opsType: op.id }))}>
            {data.opsType === op.id && <div className="ops-card__check">✓</div>}
            <div className="ops-card__icon">{op.icon}</div>
            <div className="ops-card__label">{op.label}</div>
            <div className="ops-card__desc">{op.desc}</div>
          </div>
        ))}
      </div>

      {data.opsType === "both" && (
        <div className="mb-20">
          <Notice icon="⚙">Since this airline operates both, specify whether station infrastructure is shared or managed separately.</Notice>
          <div className="chip-row">
            {["Same stations & warehouses for both","Separate stations & warehouses"].map((opt, i) => (
              <Chip key={opt} label={opt} selected={data.sharedStations === (i === 0 ? "same" : "separate")} onClick={() => setData(d => ({ ...d, sharedStations: i === 0 ? "same" : "separate" }))} />
            ))}
          </div>
        </div>
      )}

      {data.sharedStations !== "separate" ? (
        <>
          <SectionDivider label="Catering Stations" />
          <div className='instructions'>
            <span>Airports configured as catering uplift and retrieval points for this airline.</span>
          </div>
          <StationList items={warehouses} loading={whLoading} error={whError} onRetry={fetchWarehouses} />
        </>
      ) : (
        <>
          <Notice warn icon="⚠">
            The configuration steps below will be split into <strong>Catering</strong> and <strong>Duty Free</strong> tabs so you can configure each independently — including separate stations, uplift rules, galley layouts, and item categories.
          </Notice>
          <SectionDivider label="Stations & Warehouses" />
          <div className="station-radio-list">
            {STATION_TABS.map(tab => {
              const isSel = selected === tab.id;
              return (
                <label key={tab.id} className={`station-radio-row ${isSel ? "station-radio-row--selected" : ""}`} onClick={() => setSelected(tab.id)}>
                  <span className={`station-radio-dot ${isSel ? "station-radio-dot--selected" : ""}`}>
                    {isSel && <span className="station-radio-inner" />}
                  </span>
                  <span className={`station-radio-label ${isSel ? "station-radio-label--selected" : ""}`}>{tab.label}</span>
                  <span className="station-radio-hint">{tab.hint}</span>
                </label>
              );
            })}
          </div>
          <p className="text-secondary font-sm mb-14">{activeTab?.desc}</p>
          <StationList items={warehouses} loading={whLoading} error={whError} onRetry={fetchWarehouses} />
        </>
      )}

      <SectionDivider label="Select Aircraft Types" />
      <div className='instructions'>
        <span>Select all aircraft types that will be managed via the DFP Calendar. Each type will require its own configuration.</span>
      </div>

      {acLoading && <div style={{ padding:"24px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading aircraft types…</div>}

      {acError && !acLoading && (
        <div style={{ padding:"12px 16px", borderRadius:6, border:"1px solid rgba(224,92,92,0.3)", background:"rgba(224,92,92,0.06)", color:" #e05c5c", fontSize:13, marginBottom:16 }}>
          {acError}&nbsp;
          <button onClick={fetchAircraftTypes} style={{ background:"none", border:"none", color:"#4cc3e6", cursor:"pointer", fontSize:13, textDecoration:"underline" }}>Retry</button>
        </div>
      )}

      {!acLoading && !acError && (
        <div className="aircraft-grid">
          {aircraftTypes.map(ac => {
            const sel = data.selectedAircraft.includes(ac.id);
            return (
              <div key={ac.id} className={`aircraft-card ${sel ? "aircraft-card--selected" : ""}`} onClick={() => toggleAircraft(ac.id)}>
                {sel && <div className="aircraft-card__check">✓</div>}
                <div className="aircraft-card__code">{ac.code}</div>
                {ac.variant && <div className="aircraft-card__meta" style={{ fontSize:10.5, marginBottom:2 }}>{ac.variant}</div>}
                <div className="aircraft-card__meta">{ac.body}</div>
              </div>
            );
          })}
          {aircraftTypes.length === 0 && (
            <div style={{ gridColumn:"1/-1", padding:"20px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>No aircraft types found.</div>
          )}
        </div>
      )}

      {data.selectedAircraft.length > 0 && (
        <>
          <SectionDivider label="Clone Configuration" />
          <Notice icon="⚙">
            Copy catering group rules and uplift settings from an existing aircraft configuration. Select the source aircraft, choose what to copy, then click Clone.
          </Notice>
          <CloneSection
            aircraftTypes={aircraftTypes}
            selectedAircraft={data.selectedAircraft}
            data={data}
            setData={setData}
          />
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIZARD — STEP 2
───────────────────────────────────────────────────────────────────────────── */
function Step2({ data, setData }) {
  const PREC_MAP = { "Max Sectors":"maxSectors", "Max Duration":"maxSpanHours", "Whichever occurs first":"first" };
  const PREC_LABEL = Object.fromEntries(Object.entries(PREC_MAP).map(([k,v])=>[v,k]));
  const [precedence, setPrecedence] = useState(PREC_LABEL[data.limitPriority] || "Max Sectors");
  const [iropAuto, setIropAuto] = useState(data.createSubGroupOnIROP ?? true);

  const aircraft = data.selectedAircraft.length
    ? data.selectedAircraft.map(id => {
        const full = data.aircraftTypesList?.find(a => a.id === id);
        return full || { id, code: data.aircraftMeta?.[id] || id, variant:null, body:null, seats:null };
      })
    : [];

  function handlePrecedence(label) { setPrecedence(label); setData(d => ({ ...d, limitPriority: PREC_MAP[label] })); }
  function handleIropToggle() { setIropAuto(v => { const next = !v; setData(d => ({ ...d, createSubGroupOnIROP:next })); return next; }); }
  function setRule(id, field, value) {
    setData(d => ({ ...d, aircraftRules: { ...(d.aircraftRules || {}), [id]: { maxSectors:4, maxSpanHours:12, allowTopup:true, routeType:"dom", ...(d.aircraftRules?.[id] || {}), [field]:value } } }));
  }
  function getRule(id, field, fallback) { return data.aircraftRules?.[id]?.[field] ?? fallback; }

  return (
    <div>
      <Notice>A catering group is a logical grouping of consecutive flights on the same tail number that share one uplift and one retrieval.</Notice>
      <SectionDivider label="Grouping Rules — Per Aircraft Type" />
      <div className='instructions'>
        <span>These rules determine how long a catering group spans. Configure for each selected aircraft type.</span>
      </div>
      {aircraft.length === 0 ? (
        <div style={{ padding:"20px 16px", textAlign:"center", color:"#3d5a7a", fontSize:13, border:"1px dashed #28508C73", borderRadius:8, marginBottom:20 }}>
          No aircraft types selected. Go back to Step 1 and select at least one aircraft type.
        </div>
      ) : (
        <div className="table-wrap mb-20">
          <table className="data-table catering-table-main">
            <thead><tr>{["Aircraft Types","Max Sectors","Max Duration (hrs)","Allow Topup","Domestic / Intl"].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              <tr>
                <td>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {aircraft.map(ac => (
                      <span key={ac.id} className="ac-chip">{ac.code}</span>
                    ))}
                  </div>
                </td>
                <td><input type="text" inputMode="numeric" pattern="[0-9]*" className="w-70" value={getRule(aircraft[0].id,"maxSectors",4)} onChange={e => aircraft.forEach(ac => setRule(ac.id,"maxSectors",e.target.value.replace(/[^0-9]/g,"")))} /></td>
                <td><input type="text" inputMode="numeric" pattern="[0-9]*" className="w-70" value={getRule(aircraft[0].id,"maxSpanHours",12)} onChange={e => aircraft.forEach(ac => setRule(ac.id,"maxSpanHours",e.target.value.replace(/[^0-9]/g,"")))} /></td>
                <td><SimpleToggle defaultOn={getRule(aircraft[0].id,"allowTopup",true)} onChange={v => aircraft.forEach(ac => setRule(ac.id,"allowTopup",v))} /></td>
                <td>
                  <select style={{ width:130 }}
                    value={getRule(aircraft[0].id,"routeType","dom") === "int" ? "International Only" : "Both"}
                    onChange={e => aircraft.forEach(ac => setRule(ac.id,"routeType",e.target.value === "International Only" ? "int" : "dom"))}>
                    <option>Both</option><option>Domestic Only</option><option>International Only</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <SectionDivider label="Precedence Rule" />
      <FormGroup label="Which parameter should take precedence?" required>
        <div className="chip-row mt-8">
          {["Max Sectors","Max Duration","Whichever occurs first"].map(opt => (
            <Chip key={opt} label={opt} selected={precedence === opt} onClick={() => handlePrecedence(opt)} />
          ))}
        </div>
      </FormGroup>

      <SectionDivider label="IROP Handling" />
      <Toggle label="Auto-create sub-group on IROP" desc="When an IROP breaks a catering group mid-way, automatically create a smaller catering group for remaining sectors" on={iropAuto} onToggle={handleIropToggle} />

      <SectionDivider label="Sector Cancellation Handling" />
      <FormGroup label="When a mid-group sector is cancelled">
        <select defaultValue={data.sectorCancellationStrategy || "noAction"} onChange={e => setData(d => ({ ...d, sectorCancellationStrategy:e.target.value }))}>
          <option value="treatAsIROP">Treat as IROP — re-evaluate group</option>
          <option value="promptManualIntervention">Prompt catering lead to decide</option>
          <option value="noAction">No action</option>
        </select>
      </FormGroup>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIZARD — STEP 5
───────────────────────────────────────────────────────────────────────────── */
function Step5({ data, onGoStep, saved, isEdit, onBack, onStdUplift, createdId }) {
  if (saved) return (
    <div className="success-screen pop-anim">
      <div className="success-icon pop-anim">✅</div>
      <div className="success-title">{isEdit ? "Changes Saved" : "Configuration Saved Successfully"}</div>
      <div className="success-sub">
        {isEdit
          ? "Your configuration has been updated successfully."
          : "Your DFP Calendar is now configured and ready. Catering groups will be automatically created based on your rules starting from the next flight schedule sync."}
      </div>
      <div className="success-actions">
        <Btn variant="primary" onClick={onBack}>← Back to Configurations</Btn>
        {!isEdit && <Btn variant="success" onClick={() => onStdUplift(createdId, data)}>Configure Standard Uplift →</Btn>}
      </div>
    </div>
  );

  const ReviewBlock = ({ title, step, children }) => (
    <div className="review-block">
      <div className="review-block__header">
        <span className="review-block__title">{title}</span>
        <button className="review-block__edit" onClick={() => onGoStep(step)}>✎ Edit</button>
      </div>
      {children}
    </div>
  );
  const KV = ({ label, value, mono }) => (
    <div className="review-kv">
      <div className="review-k">{label}</div>
      <div className={`review-v ${mono ? "review-v--mono" : ""}`}>{value}</div>
    </div>
  );
  const aircraft = data.selectedAircraft.length ? data.selectedAircraft.map(id => data.aircraftMeta?.[id] || id) : [];

  return (
    <div>
      <ReviewBlock title="Aircraft & Airline Setup" step={1}>
        <div className="review-kv-grid">
          <KV label="Airline" value={getCultureFromLs()?.countryName} /><KV label="IATA Code" value={getCultureFromLs()?.countryCode} mono />
          <KV label="Operations" value={data.opsType==="both"?"Catering + Duty Free":data.opsType==="cateringOnly"?"Catering Only":"Duty Free Only"} />
          <KV label="Aircraft Types" value={aircraft.join(", ")} />
        </div>
      </ReviewBlock>
      <ReviewBlock title="Catering Group Config" step={2}>
        <div className="review-kv-grid">
          <KV label="Max Sectors" value="4" /><KV label="Max Duration" value="12 hrs" />
          <KV label="Precedence" value="Max Sectors" /><KV label="IROP Auto Sub-group" value="Enabled" />
        </div>
      </ReviewBlock>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIZARD PAGE
───────────────────────────────────────────────────────────────────────────── */
const STEPS = ["Aircraft & Airline","Catering Groups","Review & Save"];

function WizardPage({ onBack, onSaveConfig, onStdUplift, editData }) {
  const isEdit = !!editData;
  const [step, setStep]       = useState(1);
  const [saved, setSaved]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [createdId, setCreatedId] = useState(editData?.id || null);
  const [data, setData] = useState(() => {
    if (!editData) return { opsType:"both", sharedStations:"same", selectedAircraft:[] };
    const rules = editData.groupRules || {};
    const PREC_TO_API = { "Whichever occurs first":"first", "Max Sectors":"sectors", "Max Duration":"maxSpanHours" };
    const aircraftRules = {};
    (editData._aircraftTypeIds || []).forEach(id => {
      aircraftRules[id] = {
        maxSectors:    parseInt(rules.maxSectors)   || 4,
        maxSpanHours: parseInt(rules.maxDuration) || 12,
        allowTopup:   rules.topup === "Yes",
        routeType:    rules.type || "dom",
      };
    });
    return {
      opsType:                    editData.ops || "both",
      sharedStations:             "same",
      selectedAircraft:           editData._aircraftTypeIds || editData.aircraft?.map(a => a.id || a.code) || [],
      aircraftMeta:               Object.fromEntries((editData.aircraft || []).map(a => [a.id, a.code])),
      aircraftTypesList:          editData.aircraft || [],
      aircraftRules,
      limitPriority:              PREC_TO_API[rules.precedence] || "first",
      createSubGroupOnIROP:       rules.iropAuto === "Enabled",
      sectorCancellationStrategy: "noAction",
    };
  });

  const goStep   = n => { setStep(n); window.scrollTo(0,0); };
  const progress = ((step-1)/2)*100;

  async function getCreds() {
    const storedCreds = localStorage.getItem('login_credentials');
    const { data: creds } = JSON.parse(storedCreds) || {};
    return await parseJwtToken(creds);
  }

  function buildBody(status) {
    const firstId    = data.selectedAircraft?.[0];
    const firstRules = data.aircraftRules?.[firstId] || {};
    return {
      aircraftTypeIds:              data.selectedAircraft,
      type:                         firstRules.routeType    || data.routeType    || "dom",
      operationType:                data.opsType === "cateringOnly" ? "cateringOnly" : data.opsType === "dutyFreeOnly" ? "dutyFreeOnly" : "both",
      maxSectors:                   parseInt(firstRules.maxSectors) || parseInt(data.maxSectors) || 4,
      maxSpanHours:                 parseInt(firstRules.maxSpanHours) || parseInt(data.maxSpanHours) || 12,
      limitPriority:                data.limitPriority      || "first",
      allowTopup:                   firstRules.allowTopup   ?? data.allowTopup   ?? true,
      createSubGroupOnIROP:         data.createSubGroupOnIROP         ?? false,
      sectorCancellationStrategy:   data.sectorCancellationStrategy   || "noAction",
      enableStandardUplift:         data.enableStandardUplift         ?? false,
      enableTrolleyGalleyConfig:    data.enableTrolleyGalleyConfig    ?? false,
      allowTrolleySelfAssignByCrew: data.allowTrolleySelfAssignByCrew ?? false,
      useSealNumberTracking:        data.useSealNumberTracking        ?? false,
      status,
    };
  }

  async function postConfig(status) {
    const tokenResp = await getCreds();
    return await APIEndpoint.post('catering-group/config', buildBody(status), { user_id:tokenResp.userId, DashboardAccess:tokenResp.userType });
  }

  async function putConfig(id, status) {
    const tokenResp = await getCreds();
    return await APIEndpoint.put(`catering-group/config/${id}`, buildBody(status), { user_id:tokenResp.userId, DashboardAccess:tokenResp.userType });
  }

  async function handleStep2Continue() {
    setSaving(true);
    try {
      if (!createdId) {
        const newId = await postConfig("draft");
        if (newId) setCreatedId(newId?.data?.id);
      } else {
        const currentStatus = editData?.status || "draft";
        const res = await putConfig(createdId, currentStatus);
        if (res?.success) toastSuccess(res?.data?.message || "Success");
      }
      goStep(3);
    } catch (err) {
      console.error('step2 continue error:', err);
      const errorObj = JSON.parse(err.message);
      toastError(errorObj?.data?.message || errorObj?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFinalSave(status = null) {
    setSaving(true);
    try {
      let finalId = createdId;
      if (createdId) {
        await putConfig(createdId, status);
      } else {
        const res = await postConfig(status);
        const newId = res?.data?.id || res?.data?.data?.id;
        if (newId) { setCreatedId(newId); finalId = newId; }
        if (res?.success) toastSuccess(res?.data?.message || "Success");
      }
      onSaveConfig(data, finalId);
      setSaved(true);
    } catch (err) {
      console.error('final save error:', err);
      let msg = "Save failed. Please try again.";
      try { const e = JSON.parse(err.message); msg = e?.data?.message || e?.message || msg; } catch {}
      toastError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark fade-in page-wrap">
      <div className="wizard-page-header">
        <button className="back-btn" onClick={onBack}>← Back to {isEdit ? "Configuration" : "Configurations"}</button>
        <div className="wizard-page-title">{isEdit ? "Edit Configuration" : "DFP Configuration Setup"}</div>
        <div className="wizard-page-sub">
          {isEdit ? "Modify aircraft configuration, catering rules, uplift settings and galley layout." : "Complete all steps to create a new DFP Calendar configuration for your airline and aircraft types."}
        </div>
        {isEdit && (
          <div className="flex gap-8 items-center flex-wrap mt-8">
            {editData.aircraft.map(a => <AcChip key={a.code} code={a.code} ac={a} />)}
            <OpsBadge ops={editData.ops} label={editData.opsLabel} />
            <StatusBadge status={editData.status} />
          </div>
        )}
      </div>

      <div className="progress-wrap">
        <div className="progress-labels">
          <span>Step {step} of {STEPS.length}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="progress-track"><div className="progress-fill" style={{ width:`${progress}%` }} /></div>
      </div>

      <div className="stepper">
        {STEPS.map((label, i) => {
          const n = i+1, done = n<step, active = n===step;
          return (
            <div key={n} className="step-item" onClick={() => goStep(n)}>
              {i < STEPS.length-1 && <div className={`step-connector ${done ? "step-connector--done" : ""}`} />}
              <div className={`step-dot ${active?"step-dot--active":done?"step-dot--done":""}`}>{done?"✓":n}</div>
              <div className={`step-label ${active?"step-label--active":done?"step-label--done":""}`}>{label}</div>
            </div>
          );
        })}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div className="panel-icon">{["✈️","📦","✅"][step-1]}</div>
          <div>
            <div className="panel-title">{STEPS[step-1]}</div>
            <div className="panel-subtitle">
              {["Select aircraft types and define operational parameters for your airline","Define how flights are grouped for inventory uplift and retrieval","Review all settings before saving"][step-1]}
            </div>
          </div>
        </div>
        <div className="panel-body">
          {step===1 && <Step1 data={data} setData={setData} />}
          {step===2 && <Step2 data={data} setData={setData} />}
         {step===3 && <Step5 data={data} onGoStep={goStep} saved={saved} isEdit={isEdit} onBack={onBack} onStdUplift={onStdUplift} createdId={createdId} />}
        </div>
      </div>

      {!saved && (
        <div className="wizard-action-bar">
          <div>{step > 1 && <Btn variant="ghost" onClick={() => goStep(step-1)}>← Back</Btn>}</div>
          <div className="wizard-action-right">
            <Btn variant="ghost" onClick={onBack}>Cancel</Btn>
            {step === 1 && <Btn variant="primary" onClick={() => goStep(2)}>Continue →</Btn>}
            {step === 2 && (
              <Btn variant="primary" onClick={handleStep2Continue}>
                {saving ? "Saving…" : (createdId ? "Update & Continue →" : "Continue →")}
              </Btn>
            )}
            {step === 3 && (
              <>
                <button className="btn-draft" disabled={saving} onClick={() => handleFinalSave("draft")}>
                  {saving ? "Saving…" : "Save as Draft"}
                </button>
                <Btn variant="success" onClick={() => handleFinalSave("active")}>
                  {saving ? "Saving…" : isEdit ? "Save Changes ✓" : "Save Configuration ✓"}
                </Btn>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   GALLEY & TROLLEY TAB
───────────────────────────────────────────────────────────────────────────── */
const CLASS_CODES = [
  { value:"ecy", label:"Economy" },
  { value:"bus", label:"Business Class" },
  { value:"fst", label:"First Class" },
  { value:"pem", label:"Premium Economy" },
];

/* ─────────────────────────────────────────────────────────────────────────────
   IMPORT ERROR MODAL
───────────────────────────────────────────────────────────────────────────── */
function ImportErrorModal({ open, errors, message, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ width:580, maxHeight:"80vh", overflowY:"auto", animation:"none", transition:"none" }}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ display:"flex", alignItems:"center", gap:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e05c5c" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Import Failed
            </div>
            <div className="modal-subtitle">{message || "Please fix the following errors and re-upload."}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div>
            <table className="catering-table-main" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"rgba(224,92,92,0.1)" }}>
                  <th style={{ padding:"8px 14px", textAlign:"left", color:"#8aaacf", fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em", width:80 }}>Line</th>
                  <th style={{ padding:"8px 14px", textAlign:"left", color:"#8aaacf", fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {(errors || []).map((row, i) => (
                  <tr key={i} style={{ borderTop:"1px solid rgba(40,80,140,0.3)", background: i % 2 === 0 ? "transparent" : "rgba(13,22,40,0.4)" }}>
                    <td style={{ padding:"10px 14px", verticalAlign:"top" }}>
                      <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:6, background:"rgba(224,92,92,0.15)", color:"#e05c5c", fontFamily:"monospace", fontSize:12, fontWeight:600 }}>
                        {row.lineNumber === "All" ? "All" : `#${row.lineNumber}`}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px", verticalAlign:"top" }}>
                      {row.errors.map((e, j) => (
                        <div key={j} style={{ color:"#c8daea", lineHeight:1.6 }}>
                          {row.errors.length > 1 && <span style={{ color:"#3d5a7a", marginRight:6 }}>·</span>}
                          {e.message}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function GalleyTrolleyTab({ config, getHeaders, onRegisterSave }) {
  const aircraftTypeId = config?._aircraftTypeIds?.[0] || config?.aircraft?.[0]?.id;
  const aircraftCode   = config?.aircraft?.[0]?.code || "Aircraft";

  const [galleys,       setGalleys]       = useState([]);
  const [galleyLoading, setGalleyLoading] = useState(true);
  
  const [trolleyMap, setTrolleyMap] = useState({}); 
 
  const [masterItems, setMasterItems] = useState([]);
 
  const [selfAssign,   setSelfAssign]   = useState(config?.groupRules?.selfAssign === "Yes" || false);
  const [sealTracking, setSealTracking] = useState(config?.groupRules?.sealTracking === "Yes" || false);
 
  const [galleyModal, setGalleyModal]   = useState(false);
  const [galleyForm,  setGalleyForm]    = useState({ name:"", position:"fwd", classCodes:["ecy"], fullCarts:0, halfCarts:0, smu:0, stowage:0, imageFile:null, imageName:"" });
  const [savingGalley, setSavingGalley] = useState(false);
  const [editingGalley, setEditingGalley] = useState(null); // galley object being edited
const [importError, setImportError] = useState({ open:false, errors:[], message:"" });

  function openEditGalley(g) {
    setEditingGalley(g);
    const fullCart = g.carts?.find(c => c.id === "full")?.quantity || 0;
    const halfCart = g.carts?.find(c => c.id === "half")?.quantity || 0;
    const smu      = g.carts?.find(c => c.id === "smu")?.quantity  || 0;
    const stowage  = g.carts?.find(c => c.id === "stowage")?.quantity || 0;
    setGalleyForm({
      name:      g.name,
      position:  g.position || "fwd",
      classCodes:[g.classCode || "ecy"],
      fullCarts: fullCart,
      halfCarts: halfCart,
      smu,
      stowage,
      imageFile: null,
      imageName: "",
    });
    setGalleyModal(true);
  }

 
  const [trolleyModal,   setTrolleyModal]   = useState(null); // { galleyId, unit }
  const [itemQtyMap,     setItemQtyMap]     = useState({});   // { [itemId]: maxQuantity }
  const [savingTrolley] = useState(false);
 
  const [addTrolleyModal,  setAddTrolleyModal]  = useState(false);
  const [editingTrolley,   setEditingTrolley]   = useState(null); 
  const [trolleyForm,      setTrolleyForm]      = useState({ name:"", type:"full", itemCategory:"F&B", galleyId:"", totalQty:200 });

  const TROLLEY_CATEGORIES = ["F&B", "Beverages", "Meals", "Duty Free", "Meal Box"];
  const TROLLEY_TYPE_OPTIONS = [
    { value:"full",              label:"Full Cart" },
    { value:"half",              label:"Half Cart" },
    { value:"smu",               label:"SMU" },
    { value:"stowage",           label:"Misc. Stowage" },
    { value:"standardContainer", label:"Standard Container" },
  ];

 
  useEffect(() => {
    if (!aircraftTypeId) { setGalleyLoading(false); return; }
    fetchAircraftGalleys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftTypeId]);

 
  useEffect(() => {
    if (onRegisterSave) onRegisterSave(saveTrolleyUnitsToAPI);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trolleyMap]);

  async function fetchAircraftGalleys() {
    try {
      setGalleyLoading(true);
      const hdrs = await getHeaders();
      if (!hdrs) return;
 
      const base = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
      const res = await APIEndpoint.post('aircraftTypes/process', { ...base, limit:50 }, hdrs);
      if (res?.items?.length) {
        const match = res.items.find(ac => ac.id === aircraftTypeId);
        if (match?.galleys?.length) {
          setGalleys(match.galleys);
          const map = {};
          match.galleys.forEach(g => { map[g._id] = g.trolleyUnits || []; });
          setTrolleyMap(map);
        }
 
        try {
          const base2 = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
          const probe = await APIEndpoint.post('master/process', { ...base2, limit:10 }, hdrs);
          const total = probe?.count || probe?.items?.length || 10;
          const full2 = await APIEndpoint.post('master/process', { ...base2, limit:total }, hdrs);
          if (full2?.items?.length) {
            setMasterItems(full2.items.map(it => ({ itemId: it.itemId, itemName: it.itemName })));
          }
        } catch (err) {
          console.error('fetchMasterItems error:', err);
        }
      }
    } catch (err) {
      console.error('fetchAircraftGalleys error:', err);
    } finally {
      setGalleyLoading(false);
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function fetchTrolleyUnits(galleyId) {
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;
      const res = await APIEndpoint.get(`aircraftTypes/${aircraftTypeId}/galleys/${galleyId}/trolleyUnits`, hdrs);
      if (res?.success && res?.data) {
        setTrolleyMap(prev => ({ ...prev, [galleyId]: res.data.trolleyUnits || [] }));
      }
    } catch (err) {
      console.error('fetchTrolleyUnits error:', err);
    }
  }
 
  async function handleAddGalley() {
    if (!galleyForm.name.trim()) return;
    setSavingGalley(true);
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;

      const createCartItem = (id, name, quantity) => ({
        id, name, weight:0, volume:0,
        dimensions:{ length:0, breadth:0, height:0 },
        quantity: parseInt(quantity) || 0,
      });

      const carts = [
        createCartItem("full",    "Full Cart",     galleyForm.fullCarts),
        createCartItem("half",    "Half Cart",     galleyForm.halfCarts),
        createCartItem("smu",     "SMU",           galleyForm.smu),
        createCartItem("stowage", "Misc. Stowage", galleyForm.stowage),
      ];

      const classCode = galleyForm.classCodes?.[0] || "ecy";

      let res;
      if (editingGalley) {
 
        res = await DFPAPIEndpointGalley.post(`galley/aircraftTypes/${aircraftTypeId}/galleys`, {
          name:            galleyForm.name.trim(),
          classCode,
          carts,
          galleyCartConfig: editingGalley.galleyCartConfig || { userDefined: [], configured: [] },
          audit:           editingGalley.audit || undefined,
          trolleyUnits:    editingGalley.trolleyUnits || [],
          galleyId:        editingGalley._id,
        }, hdrs);
      } else {
 
        res = await DFPAPIEndpointGalley.post(`galley/aircraftTypes/${aircraftTypeId}/galleys`, {
          name:      galleyForm.name.trim(),
          classCode,
          carts,
        }, hdrs);
      }

      if (res?.success) {
        toastSuccess(res.message || (editingGalley ? 'Galley updated successfully.' : 'Galley added successfully.'));
        setGalleyModal(false);
        setEditingGalley(null);
        setGalleyForm({ name:"", position:"fwd", classCodes:["ecy"], fullCarts:0, halfCarts:0, smu:0, stowage:0, imageFile:null, imageName:"" });
        await fetchAircraftGalleys();
      }
    } catch (err) {
      console.error('addGalley error:', err);
      const e = JSON.parse(err.message);
      toastError(e?.data?.message || 'Failed to save galley.');
    } finally {
      setSavingGalley(false);
    }
  }
 
  async function handleDeleteGalley(galleyId) {
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;
      const res = await DFPAPIEndpointGalley.delete(`galley/aircraftTypes/${aircraftTypeId}/galleys/${galleyId}`, hdrs);
      if (res?.success) {
        toastSuccess(res.message || 'Galley removed.');
        setGalleys(prev => prev.filter(g => g._id !== galleyId));
        setTrolleyMap(prev => { const n = {...prev}; delete n[galleyId]; return n; });
      }
    } catch (err) {
      console.error('deleteGalley error:', err);
      const e = JSON.parse(err.message);
      toastError(e?.data?.message || 'Failed to remove galley.');
    }
  }
 
  function openTrolleyModal(galleyId, unit) {
    const existing = {};
    (unit.items || []).forEach(it => {
      if (String(it.itemId) !== "totalQty") existing[String(it.itemId)] = it.maxQuantity || 0;
    });
    setItemQtyMap(existing);
    setTrolleyModal({ galleyId, unit });
  }

  function openAddTrolleyModal(presetGalleyId) {
    setEditingTrolley(null);
    setTrolleyForm({
      name:         "",
      type:         "full",
      itemCategory: "F&B",
      galleyId:     presetGalleyId || (galleys[0]?._id || ""),
      totalQty:     200,
    });
    setAddTrolleyModal(true);
  }

  function openEditTrolleyModal(u) {
    setEditingTrolley(u);
    const sentinel = (u.items || []).find(it => String(it.itemId) === "totalQty");
    setTrolleyForm({
      name:         u.name,
      type:         u.type || "full",
      itemCategory: u.itemCategory || "F&B",
      galleyId:     u.galleyId,
      totalQty:     sentinel?.maxQuantity ?? 200,
    });
    setAddTrolleyModal(true);
  }
 
  function handleAddOneTrolley() {
    if (!trolleyForm.name.trim() || !trolleyForm.galleyId) return;
    const galleyId = trolleyForm.galleyId;
    const unitData = {
      type:         trolleyForm.type,
      name:         trolleyForm.name.trim(),
      itemCategory: trolleyForm.itemCategory,
      items:        [{ itemId: "totalQty", maxQuantity: Number(trolleyForm.totalQty) || 0 }],
    };

    if (editingTrolley) {
 
      setTrolleyMap(prev => {
        const updated = {};
        for (const [gId, units] of Object.entries(prev)) {
          updated[gId] = units.map(u => {
            const match = u._id ? u._id === editingTrolley._id : u._key === editingTrolley._key;
            if (!match) return u;
            return { ...u, ...unitData, galleyId };
          });
        }
 
        if (galleyId !== editingTrolley.galleyId) {
          const oldGid = editingTrolley.galleyId;
          updated[oldGid] = (updated[oldGid] || []).filter(u =>
            u._id ? u._id !== editingTrolley._id : u._key !== editingTrolley._key
          );
          const movedUnit = { ...editingTrolley, ...unitData, galleyId };
          updated[galleyId] = [...(updated[galleyId] || []), movedUnit];
        }
        return updated;
      });
      toastSuccess(`Trolley "${unitData.name}" updated.`);
    } else {
 
      const newUnit = { _key: Date.now(), ...unitData };
      setTrolleyMap(prev => ({ ...prev, [galleyId]: [...(prev[galleyId] || []), newUnit] }));
      toastSuccess(`Trolley "${newUnit.name}" added. Click Configure → Save to persist.`);
    }

    setAddTrolleyModal(false);
    setEditingTrolley(null);
    setTrolleyForm({ name:"", type:"full", itemCategory:"F&B", galleyId:"", totalQty:200 });
  }

 
  function handleSaveTrolleyUnits(galleyId) {
    if (!trolleyModal?.unit) return;
    const unit  = trolleyModal.unit;
    const items = Object.entries(itemQtyMap)
      .filter(([id, qty]) => !id.startsWith('__new_') && id !== "totalQty" && Number(qty) > 0)
      .map(([itemId, maxQuantity]) => ({ itemId: String(itemId), maxQuantity: Number(maxQuantity) }));

 
    setTrolleyMap(prev => ({
      ...prev,
      [galleyId]: (prev[galleyId] || []).map(u =>
        ((u._id && u._id === unit._id) || (u._key && u._key === unit._key))
          ? { ...u, items }
          : u
      ),
    }));
    setTrolleyModal(null);
    toastSuccess('Trolley configuration saved locally. Click "Save & Activate" to persist.');
  }

 
  async function saveTrolleyUnitsToAPI() {
    const hdrs = await getHeaders();
    if (!hdrs) return;
    let lastMessage = 'Trolley units saved successfully';
    for (const galleyId of Object.keys(trolleyMap)) {
      const units = trolleyMap[galleyId] || [];
          if (!units.length) {
      await APIEndpoint.post(
        `aircraftTypes/${aircraftTypeId}/galleys/${galleyId}/trolleyUnits`,
        { trolleyUnits: [] },
        hdrs
      );
      continue;
    }
      const res = await APIEndpoint.post(
        `aircraftTypes/${aircraftTypeId}/galleys/${galleyId}/trolleyUnits`,
        {
          trolleyUnits: units.map(u => ({
            ...(u._id ? { _id: u._id } : {}),
            type:         u.type,
            name:         u.name,
            itemCategory: u.itemCategory || undefined,
            items:        (u.items || []).filter(it => String(it.itemId) !== "totalQty"),
          })),
        },
        hdrs
      );
      if (!res?.success) {
        throw new Error(JSON.stringify({ data: { message: res?.message || `Failed to save trolley units for galley ${galleyId}` } }));
      }
      if (res?.message) lastMessage = res.message;
    }
    return lastMessage;
  }

  // eslint-disable-next-line no-unused-vars
  function addTrolleyRow(galleyId) {
    setTrolleyMap(prev => ({
      ...prev,
      [galleyId]: [...(prev[galleyId] || []), { _key: Date.now(), name:"", type:"full", itemCategory:"", items:[], totalQty:0 }]
    }));
  }

  // eslint-disable-next-line no-unused-vars
  function updateTrolleyRow(galleyId, _key, field, val) {
    setTrolleyMap(prev => ({
      ...prev,
      [galleyId]: (prev[galleyId] || []).map(u => (u._key || u._id) === _key ? { ...u, [field]: val } : u)
    }));
  }

  // eslint-disable-next-line no-unused-vars
  function removeTrolleyRow(galleyId, _key) {
    setTrolleyMap(prev => ({
      ...prev,
      [galleyId]: (prev[galleyId] || []).filter(u => (u._key || u._id) !== _key)
    }));
  }

 
  const allTrolleys = galleys.flatMap(g => {
    return (trolleyMap[g._id] || []).map(u => ({
      ...u,
      galleyName: g.name,
      galleyId:   g._id,
      _rowKey:    u._id || u._key || `${g._id}-${u.name}`,
    }));
  });

  const classLabel = code => CLASS_CODES.find(c => c.value === code)?.label || code;


  return (
    <div className="panel mb-20">
      <div className="panel-header">
        <div className="panel-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
          </svg>
        </div>
        <div>
          <div className="panel-title">Galley & Trolley Configuration</div>
          <div className="panel-subtitle">Galley layout and trolley loading plans</div>
        </div>
      </div>

      <div className="panel-body">
 
        <div className="form-grid-2" style={{ marginBottom:20 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>
              Aircraft Type <span style={{ color:"#e05c5c" }}>*</span>
            </div>
            <div style={{ padding:"10px 14px", borderRadius:7, border:"1px solid rgba(40,80,140,0.45)", background:"#0c1526", fontSize:13.5, color:"#d6eaf8" }}>
              {aircraftCode}{config?.aircraft?.[0]?.body ? ` (${config.aircraft[0].body})` : ""}
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Body Type</div>
            <div style={{ padding:"10px 14px", borderRadius:7, border:"1px solid rgba(40,80,140,0.45)", background:"#0c1526", fontSize:13.5, color:"#d6eaf8" }}>
              {config?.aircraft?.[0]?.body || "—"}
            </div>
          </div>
        </div>

  
        <SectionDivider label="Aircraft Galley Setup" />
        {galleyLoading ? (
          <div style={{ padding:"20px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading galleys…</div>
        ) : (
          <div className="mb-16" style={{ display:"flex", flexDirection:"row", flexWrap:"wrap", gap:12 }}>
            {galleys.map(g => (
              <div key={g._id} style={{
                position:"relative",
                width:220, flexShrink:0,
                padding:"14px 16px", borderRadius:8,
                border:"1px solid rgba(40,80,140,0.45)", background:"#0d1628",
              }}>
       
                <button
                  onClick={() => handleDeleteGalley(g._id)}
                  style={{ position:"absolute", top:8, right:8, background:"none", border:"none", color:"#3d5a7a", cursor:"pointer", fontSize:16, lineHeight:1, padding:"2px 4px" }}
                  title="Remove galley"
                >×</button>
                <div style={{ color:"#4cc3e6", fontWeight:600, fontSize:14, marginBottom:4, paddingRight:20 }}>{g.name}</div>
                <div style={{ color:"#8aaacf", fontSize:12, marginBottom:2 }}>Class: {classLabel(g.classCode)}</div>
                <div style={{ color:"#3d5a7a", fontSize:12, marginBottom:10 }}>
                  {(g.carts || []).reduce((s, c) => s + (Number(c.quantity) || 0), 0)} total carts
                </div>
                <button
                  className="btn btn-ghost"
                  style={{ padding:"4px 12px", fontSize:11, color:"#4cc3e6", width:"100%" }}
                  onClick={() => openEditGalley(g)}
                >✎ Edit Galley</button>
              </div>
            ))}
 
            <div
              style={{width:220, flexShrink:0, minHeight:110,}}
              className="add-row-btn flexCol justifyContentCenter alignItemsCenter"
              onClick={() => { setEditingGalley(null); setGalleyForm({ name:"", position:"fwd", classCodes:["ecy"], fullCarts:0, halfCarts:0, smu:0, stowage:0, imageFile:null, imageName:"" }); setGalleyModal(true); }}
            >
              <span>+</span>
              <span>Add Galley</span>
            </div>
          </div>
        )}

        {/* ── Trolley Type Configuration ── */}
        <SectionDivider label="Trolley Type Configuration" />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, gap:12 }}>
          <div  className="panel-subtitle">Configure trolley assignments and item capacities</div>
          <div style={{ display:"flex", gap:8, flexShrink:0 }}>
    <button className="btn btn-ghost" style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px", fontSize:13 }}
                  onClick={async () => {
            try {
              const hdrs = await getHeaders();
              if (!hdrs) return;
              const header = {
                ...APIEndpoint.getHeaders().headers,
                user_id: hdrs.user_id,
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              };
              const res = await fetch(
                APIEndpoint.getFullPath(`aircraftTypes/${aircraftTypeId}/galleys/${galleys[0]?._id}/trolleyUnits/template`),
                { method: 'GET', responseType: 'arrayBuffer', headers: header }
              );
              const blob = await res.blob();
              saveAs(blob, 'trolley-units-template.xlsx');
            } catch (err) {
              console.error('download template error:', err);
              toastError('Failed to download template.');
            }
          }}
              >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download Template
            </button>
            <button className="btn btn-primary-download" style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px", fontSize:13 }}
             onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".csv,.xlsx,.xls";
              input.onchange = async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!galleys.length) { toastError("No galleys found. Add a galley first."); return; }
                try {
                  const hdrs = await getHeaders();
                  if (!hdrs) return;
                  const formData = new FormData();
                  formData.append("file", file);
                  formData.append("mode", "replace");
                  const header = {
                    ...APIEndpoint.getHeaders().headers,
                    user_id: hdrs.user_id,
                  };
                  const res = await APIEndpoint.post(
                    `aircraftTypes/${aircraftTypeId}/galleys/${galleys[0]?._id}/trolleyUnits/upload`,
                    formData,
                    header
                  );
                  if (res?.success) {
                    toastSuccess(res.message || `Imported ${res.counts?.trolleyUnitsImported || 0} trolley unit(s).`);
                    await fetchAircraftGalleys();
                  } else {
                    if (res?.data?.errors?.length) {          
                                        setImportError({ open:true, errors: res?.data?.errors, message: res?.data?.message });
                    } else {
                      toastError(res?.message || 'Import failed.');
                    }
                  }
                } catch (err) {
                    const eEr = JSON.parse(err.message);
                   setImportError({ open:true, errors: eEr?.data?.errors, message: eEr?.data?.message });
                }
              };
              input.click();
            }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Import CSV / XLSX
            </button>
          </div>
        </div>
        <div style={{ maxHeight:280, overflowY:"auto", border:"1px solid rgba(40,80,140,0.3)", borderRadius:8, marginBottom:8 }}>
          <table className="data-table mb-8 catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
      <colgroup>
        <col style={{ width:"18%" }} />
        <col style={{ width:"13%" }} />
        <col style={{ width:"16%" }} />
        <col style={{ width:"14%" }} />
        <col style={{ width:"16%" }} />
        <col style={{ width:"23%" }} />
      </colgroup>
      <thead>
        <tr>{["Trolley Name","Trolley Type","Category","Total Item Qty","Galley","Actions"].map(h => <th key={h}>{h}</th>)}</tr>
      </thead>
          <tbody>
            {allTrolleys.length === 0 ? (
              <tr><td colSpan={6} style={{ padding:"24px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>No trolleys configured. Click "+ Add Trolley" below to start.</td></tr>
            ) : allTrolleys.map(u => (
              <tr key={u._rowKey}>
                <td style={{ fontFamily:"monospace", fontSize:13 }}>{u.name}</td>
                <td>{TROLLEY_TYPE_OPTIONS.find(t => t.value === u.type)?.label || u.type}</td>
                <td>{u.itemCategory ? <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600, border:"1px solid rgba(76,195,230,0.4)", color:"#4cc3e6" }}>{u.itemCategory}</span> : "—"}</td>
                <td style={{ color:"#4cc3e6", fontFamily:"monospace" }}>{
                  (() => {
                    const sentinel = (u.items || []).find(it => String(it.itemId) === "totalQty");
                    if (sentinel) return sentinel.maxQuantity;
                    const sum = (u.items || []).filter(it => String(it.itemId) !== "totalQty").reduce((s, it) => s + (Number(it.maxQuantity) || 0), 0);
                    return sum || "—";
                  })()
                }</td>
                <td style={{ color:"#8aaacf" }}>{u.galleyName}</td>
                <td style={{minWidth:240,whiteSpace:'nowrap'}}>
                  <div style={{ display:"flex", gap:6, alignItems:"center"}}>
                    <button
                      className="btn btn-ghost"
                      style={{ padding:"4px 12px", fontSize:12, color:"#4cc3e6" }}
                      onClick={() => openTrolleyModal(u.galleyId, u)}
                    >Configure →</button>
                    <button
                      className="btn btn-ghost"
                      style={{ padding:"4px 10px", fontSize:12, color:"#8aaacf" }}
                      onClick={() => openEditTrolleyModal(u)}
                    >✎</button>
                    <button
                      onClick={() => {
                        setTrolleyMap(prev => ({
                          ...prev,
                          [u.galleyId]: (prev[u.galleyId] || []).filter(t =>
                            t._id ? t._id !== u._id : t._key !== u._key
                          ),
                        }));
                        toastSuccess(`Trolley "${u.name}" removed.`);
                      }}
                      style={{ padding:"4px 8px", border:"none", background:"none", color:"#e05c5c", fontSize:16, cursor:"pointer", lineHeight:1 }}
                      title="Delete trolley"
                    >×</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <button className="add-row-btn mb-20" onClick={() => galleys.length ? openAddTrolleyModal(galleys[0]._id) : toastError("Add a galley first.")}>
          <span className="add-row-btn__icon">+</span> Add Trolley
        </button>
 
        <SectionDivider label="Crew Assignment" />
        <Toggle label="Self-assigned trolleys by crew" desc="Crew members self-select trolleys before beginning sales." on={selfAssign} onToggle={() => setSelfAssign(v => !v)} />
        <Toggle label="Seal number tracking required" desc="Require crew to enter trolley seal numbers at uplift and retrieval." on={sealTracking} onToggle={() => setSealTracking(v => !v)} />
      </div>
 
      {addTrolleyModal && (
        <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && setAddTrolleyModal(false)}>
          <div className="modal-box" style={{ width:520, animation:"none", transition:"none" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{editingTrolley ? "Edit Trolley" : "Add Trolley"}</div>
                <div className="modal-subtitle">{editingTrolley ? `Editing ${editingTrolley.name}` : "Define a new trolley position in this galley"}</div>
              </div>
              <button className="modal-close" onClick={() => { setAddTrolleyModal(false); setEditingTrolley(null); }}>×</button>
            </div>
            <div className="modal-body">
 
              <div className="form-grid-2" style={{ marginBottom:16 }}>
                <FormGroup label="Trolley Name" required>
                  <input
                    type="text"
                    placeholder="e.g. T-FWD-03"
                    value={trolleyForm.name}
                    onChange={e => setTrolleyForm(f => ({ ...f, name: e.target.value }))}
                  />
                </FormGroup>
                <FormGroup label="Trolley Type" required>
                  <select
                    value={trolleyForm.type}
                    onChange={e => setTrolleyForm(f => ({ ...f, type: e.target.value }))}
                  >
                    {TROLLEY_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </FormGroup>
              </div>
 
              <div className="form-grid-2" style={{ marginBottom:16 }}>
                <FormGroup label="Category">
                  <select
                    value={trolleyForm.itemCategory}
                    onChange={e => setTrolleyForm(f => ({ ...f, itemCategory: e.target.value }))}
                  >
                    {TROLLEY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormGroup>
                <FormGroup label="Assign to Galley">
                  <select
                    value={trolleyForm.galleyId}
                    onChange={e => setTrolleyForm(f => ({ ...f, galleyId: e.target.value }))}
                  >
                    {galleys.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                  </select>
                </FormGroup>
              </div>
 
              <FormGroup label="Total Item Quantity" hint="Maximum total items this trolley can hold">
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*"
                  value={trolleyForm.totalQty ?? ""}
                  style={{ width:160 }}
                  onChange={e => setTrolleyForm(f => ({ ...f, totalQty: e.target.value.replace(/[^0-9]/g,"") }))}
                />
              </FormGroup>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => { setAddTrolleyModal(false); setEditingTrolley(null); }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleAddOneTrolley}
                disabled={!trolleyForm.name.trim() || !trolleyForm.galleyId}
              >
                {editingTrolley ? "Save Changes →" : "Add Trolley →"}
              </button>
            </div>
          </div>
        </div>
      )}
 
      {galleyModal && (
        <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && setGalleyModal(false)}>
          <div className="modal-box" style={{ width:560, maxHeight:"85vh", overflowY:"auto", animation:"none", transition:"none" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{editingGalley ? "Edit Galley" : "Add Galley"}</div>
                <div className="modal-subtitle">{editingGalley ? `Editing ${editingGalley.name}` : "Define a new galley position for this aircraft type"}</div>
              </div>
              <button className="modal-close" onClick={() => { setGalleyModal(false); setEditingGalley(null); }}>×</button>
            </div>
            <div className="modal-body">
 
              <div className="form-grid-2" style={{ marginBottom:18 }}>
                <FormGroup label="Galley Name" required>
                  <input type="text" placeholder="e.g. FWD Galley" value={galleyForm.name}
                    onChange={e => setGalleyForm(f => ({ ...f, name: e.target.value }))} />
                </FormGroup>
                <FormGroup label="Position" required>
                  <select value={galleyForm.position} onChange={e => setGalleyForm(f => ({ ...f, position: e.target.value }))}>
                    <option value="fwd">FWD — Forward</option>
                    <option value="mid">MID — Middle</option>
                    <option value="aft">AFT — Aft</option>
                  </select>
                </FormGroup>
              </div>

 
              <FormGroup label="Cabin Class (Multi-Select)" required>
                <div className="chip-row mt-8">
                  {CLASS_CODES.map(c => (
                    <div
                      key={c.value}
                      className={`chip ${(galleyForm.classCodes || []).includes(c.value) ? "chip--selected" : ""}`}
                      onClick={() => setGalleyForm(f => {
                        const cur = f.classCodes || [];
                        return { ...f, classCodes: cur.includes(c.value) ? cur.filter(x => x !== c.value) : [...cur, c.value] };
                      })}
                    >{c.label}</div>
                  ))}
                </div>
              </FormGroup>
 
              <div className="form-grid-2" style={{ marginTop:18 }}>
                <FormGroup label="No. of Full Cart Trolleys">
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.fullCarts ?? ""}
                    onChange={e => setGalleyForm(f => ({ ...f, fullCarts: e.target.value.replace(/[^0-9]/g,"") }))} />
                </FormGroup>
                <FormGroup label="No. of Half Cart Trolleys">
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.halfCarts ?? ""}
                    onChange={e => setGalleyForm(f => ({ ...f, halfCarts: e.target.value.replace(/[^0-9]/g,"") }))} />
                </FormGroup>
                <FormGroup label="No. of SMU Units" hint="Standard Modular Units for small items">
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.smu ?? ""}
                    onChange={e => setGalleyForm(f => ({ ...f, smu: e.target.value.replace(/[^0-9]/g,"") }))} />
                </FormGroup>
                <FormGroup label="No. of Miscellaneous Storage" hint="Overhead bins, drawers, etc.">
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={galleyForm.stowage ?? ""}
                    onChange={e => setGalleyForm(f => ({ ...f, stowage: e.target.value.replace(/[^0-9]/g,"") }))} />
                </FormGroup>
              </div>

 
              <div style={{ marginTop:18 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>
                  Galley Image <span style={{ color:"#3d5a7a", fontWeight:400, textTransform:"none", fontSize:11 }}>(Optional)</span>
                </div>
                <div
                  style={{ border:"2px dashed rgba(40,80,140,0.6)", borderRadius:8, padding:"28px 20px", textAlign:"center", cursor:"pointer", background:"#0c1526" }}
                  onClick={() => document.getElementById("galleyImageInput").click()}
                  onMouseOver={e => e.currentTarget.style.borderColor="rgba(76,195,230,0.5)"}
                  onMouseOut={e => e.currentTarget.style.borderColor="rgba(40,80,140,0.6)"}
                >
                  <input id="galleyImageInput" type="file" accept="image/jpeg,image/png,image/webp" style={{ display:"none" }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setGalleyForm(f => ({ ...f, imageFile: file, imageName: file.name }));
                    }} />
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3d5a7a" strokeWidth="1.5" style={{ marginBottom:10 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                  </svg>
                  {galleyForm.imageName ? (
                    <div style={{ fontSize:13, color:"#4cc3e6" }}>{galleyForm.imageName}</div>
                  ) : (
                    <>
                      <div style={{ fontSize:13, color:"#8aaacf", marginBottom:6 }}>Click to upload galley layout image</div>
                      <div style={{ fontSize:11, color:"#3d5a7a", lineHeight:1.6 }}>
                        Format: JPG, PNG or WebP · Aspect ratio: <strong style={{ color:"#4cc3e6" }}>16:9</strong> recommended<br />
                        Max size: <strong style={{ color:"#4cc3e6" }}>5 MB</strong> · Min resolution: <strong style={{ color:"#4cc3e6" }}>1280 × 720px</strong><br />
                        Use a top-down galley diagram or aircraft cross-section view
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => { setGalleyModal(false); setEditingGalley(null); setGalleyForm({ name:"", position:"fwd", classCodes:["ecy"], fullCarts:0, halfCarts:0, smu:0, stowage:0 }); }} disabled={savingGalley}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddGalley} disabled={savingGalley || !galleyForm.name.trim()}>
                {savingGalley ? "Saving…" : editingGalley ? "Save Changes →" : "Add Galley →"}
              </button>
            </div>
          </div>
        </div>
      )}

 
      {trolleyModal && (() => {
        const unit     = trolleyModal.unit;
        const galleyId = trolleyModal.galleyId;
        const maxLimit = Number(unit.items?.find(it => it.itemId === "totalQty")?.maxQuantity) || 200;
        const configuredItems = Object.entries(itemQtyMap)
          .filter(([id]) => id !== "totalQty")
          .map(([id, qty]) => ({ itemId: id, maxQuantity: Number(qty) || 0 }));
        const totalConfigured = configuredItems.reduce((s, it) => s + it.maxQuantity, 0);
        const isOverLimit     = totalConfigured > maxLimit;

        const masterOptions = masterItems.map(m => ({ value: String(m.itemId), label: m.itemName }));

        return (
          <div className="modal-overlay" style={{ animation:"none", transition:"none" }} onClick={e => e.target === e.currentTarget && setTrolleyModal(null)}>
            <div className="modal-box" style={{ width:620, maxHeight:"85vh", overflowY:"auto", animation:"none", transition:"none" }}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">
                    Configure Trolley — <span style={{ color:"#4cc3e6", fontFamily:"monospace" }}>{unit.name}</span>
                  </div>
                  <div className="modal-subtitle">Set max quantity per item that can be loaded into this trolley</div>
                </div>
                <button className="modal-close" onClick={() => setTrolleyModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <table className="data-table mb-8 catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
                  <colgroup>
                    <col style={{ width:"60%" }} />
                    <col style={{ width:"30%" }} />
                    <col style={{ width:"10%" }} />
                  </colgroup>
                  <thead>
                    <tr>{["Item / Category","Max Qty in Trolley",""].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {configuredItems.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ padding:"20px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>
                          No items yet. Click "+ Add item" below to start.
                        </td>
                      </tr>
                    ) : configuredItems.map((it, idx) => {
                      const rowOptions = masterOptions.filter(
                        opt => opt.value === it.itemId ||
                          !configuredItems.some((ci, ci2) => ci2 !== idx && ci.itemId === opt.value)
                      );
                      const selectedOpt = masterOptions.find(o => o.value === it.itemId) || null;
                      return (
                        <tr key={it.itemId || idx}>
                          <td style={{ overflow:"visible" }}>
                            <CustomSelect
                              placeHolder="Select item"
                              controlStyle={{ border:"1px solid rgba(40,80,140,0.45)", borderRadius:7 }}
                              options={rowOptions}
                              value={selectedOpt}
                              onChange={opt => {
                                setItemQtyMap(prev => {
                                  const next = { ...prev };
                                  if (it.itemId) delete next[it.itemId];
                                  if (opt) next[String(opt.value)] = prev[it.itemId] || 0;
                                  return next;
                                });
                              }}
                              isClearable={true}
                              isSearchable={true}
                              menuPortalTarget={null}
                              menuPosition="absolute"
                              width="100%"
                              autoWidth={true}
                            />
                          </td>
                          <td>
                            <input
                              type="text" inputMode="numeric" pattern="[0-9]*"
                              style={{ width:"100%", border: isOverLimit ? "1px solid #e05c5c" : undefined }}
                              value={itemQtyMap[it.itemId] ?? ""}
                              onChange={e => setItemQtyMap(prev => ({ ...prev, [it.itemId]: e.target.value.replace(/[^0-9]/g,"") }))}
                            />
                          </td>
                          <td>
                            <button
                              onClick={() => setItemQtyMap(prev => { const n = {...prev}; delete n[it.itemId]; return n; })}
                              style={{ padding:"4px 8px", border:"none", background:"none", color:"#e05c5c", fontSize:16, cursor:"pointer", lineHeight:1 }}
                              title="Remove item"
                            >×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button
                  className="add-row-btn mb-12"
                  onClick={() => {
      
                    setItemQtyMap(prev => ({ ...prev, [`__new_${Date.now()}`]: 0 }));
                  }}
                >
                  <span className="add-row-btn__icon">+</span> Add item
                </button>
 
                <div style={{ fontSize:13, color:"#3d5a7a", marginTop:4 }}>
                  Total configured:{" "}
                  <span style={{ color: isOverLimit ? "#e05c5c" : "#4cc3e6", fontWeight:600, fontFamily:"monospace" }}>
                    {totalConfigured}
                  </span>
                  {" "}items
                  {isOverLimit && (
                    <span style={{ color:"#e05c5c", marginLeft:8, fontSize:12 }}>
                      (exceeds max {maxLimit})
                    </span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setTrolleyModal(null)} disabled={savingTrolley}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSaveTrolleyUnits(galleyId)}
                  disabled={savingTrolley || isOverLimit}
                >
                  {savingTrolley ? "Saving…" : "Save Configuration"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ImportErrorModal
      open={importError.open}
      errors={importError.errors}
      message={importError.message}
      onClose={() => setImportError({ open:false, errors:[], message:"" })}
      />

    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STANDARD UPLIFT PAGE EDIT and ADDing
───────────────────────────────────────────────────────────────────────────── */
function StdUpliftPage({ config, onBack, closeOnSave = true }) {
  const [activeTab, setActiveTab]       = useState("items");
  const [commitThresh,          setCommitThresh]          = useState(5);
  const [salesCommitEnabled,    setSalesCommitEnabled]    = useState(true);
  const [prebookThreshold,      setPrebookThreshold]      = useState(15);
  const [thresholdBreachAction, setThresholdBreachAction] = useState("ALERT");
  const [activationDate,        setActivationDate]        = useState("2026-04-01");
  const [expiryDate,            setExpiryDate]            = useState("2027-04-01");
  const [longHaulEnabled, setLongHaulEnabled] = useState(false);
  const [longHaulHours, setLongHaulHours]     = useState(config?.longHaulThresholdHours || 6);
 
  const [defaultRecord, setDefaultRecord] = useState({ id: null, items: [] });
  const [longRecord,    setLongRecord]    = useState({ id: null, items: [] });

  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [importError, setImportError] = useState({ open:false, errors:[], message:"" });

  const configId = config?.id;
  const galleySaveRef = { current: null }; 

  async function getHeaders() {
    const storedCreds = localStorage.getItem('login_credentials');
    if (!storedCreds) return null;
    const parsedCreds = JSON.parse(storedCreds);
    const { data } = parsedCreds || {};
    const tokenResp = await parseJwtToken(data);
    if (!tokenResp?.userId) return null;
    return { user_id: tokenResp.userId, DashboardAccess: tokenResp.userType };
  }

  function mapItems(rawItems = []) {
    return rawItems.map((it, i) => ({ _key: Date.now() + i, itemId: it.itemId, quantity: it.quantity }));
  }

  useEffect(() => {
    if (!configId) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true);
        const hdrs = await getHeaders();
        if (!hdrs) return;

        try {
          const cfgRes = await APIEndpoint.get(`catering-group/config/${configId}`, hdrs);
          if (cfgRes?.success && cfgRes?.data) {
            // setConfigDetail(cfgRes.data);
            if (cfgRes.data.longHaulThresholdHours != null) {
              setLongHaulHours(cfgRes.data.longHaulThresholdHours);
            }
            if (cfgRes.data.prebookThreshold != null) {
              setPrebookThreshold(cfgRes.data.prebookThreshold);
            }
            if (cfgRes.data.allowedDiscrepancyThreshold != null) {
              setCommitThresh(Number(cfgRes.data.allowedDiscrepancyThreshold));
            }
            if (cfgRes.data.thresholdBreachAction != null) {
              setThresholdBreachAction(cfgRes.data.thresholdBreachAction);
            }
            if (cfgRes.data.activationDate) {
              setActivationDate(new Date(cfgRes.data.activationDate).toISOString().split("T")[0]);
            }
            if (cfgRes.data.expiryDate) {
              setExpiryDate(new Date(cfgRes.data.expiryDate).toISOString().split("T")[0]);
            }
          }
        } catch (err) {
          console.error('fetchConfigDetail error:', err);
        }

        const base = { action:4, collection:"", filterOperation:"or", filters:[], page:1, searchParam:"" };
        const probe = await APIEndpoint.post('master/process', { ...base, limit:10 }, hdrs);
        const total = probe?.count || probe?.items?.length || 10;
        const full  = await APIEndpoint.post('master/process', { ...base, limit:total }, hdrs);
        if (full?.items?.length) {
          setMasterItems(full.items.map(it => ({ itemId: it.itemId, itemName: it.itemName, itemCode: it.itemCode })));
        }


        try {
          const res = await APIEndpoint.get(`catering-group/config/${configId}/standard-uplift`, hdrs);
          if (res?.success && Array.isArray(res.items)) {
            res.items.forEach(doc => {
              if (doc.haulType === "long") {
                setLongRecord({ id: doc.id, items: mapItems(doc.items) });
                setLongHaulEnabled(true);
              } else {
                setDefaultRecord({ id: doc.id, items: mapItems(doc.items) });
              }
            });
          }
        } catch (err) {
          if (err?.response?.status !== 404) console.error('fetchStdUplift error:', err);
        }
      } catch (err) {
        console.error('StdUpliftPage init error:', err);
      } finally {
        setLoading(false);
      }
    })();
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);


  async function handleSave(activate = false) {
    if (!configId) return;
    setSaving(true);
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;
 
      if (activeTab === "galley") {
        if (activate && galleySaveRef.current) {
          try {
            const apiMsg = await galleySaveRef.current();
            toastSuccess(apiMsg || 'Galley & trolley configuration saved.');
            if (closeOnSave) onBack();
          } catch (e) {
            console.error('galley save error:', e);
            let msg = 'Failed to save galley configuration.';
            try { const eg = JSON.parse(e.message); msg = eg?.data?.message || eg?.message || msg; } catch {}
            toastError(msg);
 
          }
        } else {
          toastSuccess('Galley & trolley configuration saved locally.');
        }
        return;
      }

      if (activeTab === "config") {
        const res = await APIEndpoint.put(`catering-group/config/${configId}`, {
          prebookThreshold:      Number(prebookThreshold),
          thresholdBreachAction: thresholdBreachAction,
            ...(salesCommitEnabled ? { allowedDiscrepancyThreshold: Number(commitThresh) } : {}),
          ...(activationDate ? { activationDate: new Date(activationDate).toISOString() } : {}),
          ...(expiryDate     ? { expiryDate:     new Date(expiryDate).toISOString()     } : {}),
        }, hdrs);
        toastSuccess(res?.message || 'Uplift configuration saved successfully.');
        if (activate) onBack();
        return;
      }

    
      if (longHaulEnabled) {
        await APIEndpoint.put(`catering-group/config/${configId}`, {
          longHaulThresholdHours: Number(longHaulHours),
        }, hdrs);
      }

      const defaultPayload = {
        haulType: "default",
        items: defaultRecord.items
          .filter(it => it.itemId !== null && it.itemId !== undefined && it.itemId !== "")
          .map(it => ({ itemId: Number(it.itemId), quantity: Number(it.quantity) || 0 })),
      };
      let defRes;
      if (defaultRecord.id) {
        defRes = await APIEndpoint.put(`catering-group/config/${configId}/standard-uplift`, defaultPayload, hdrs);
      } else {
        defRes = await APIEndpoint.post(`catering-group/config/${configId}/standard-uplift`, defaultPayload, hdrs);
      }
      if (defRes?.data?.id) setDefaultRecord(prev => ({ ...prev, id: defRes.data.id }));

      if (longHaulEnabled) {
        const longPayload = {
          haulType: "long",
          items: longRecord.items
            .filter(it => it.itemId !== null && it.itemId !== undefined && it.itemId !== "")
            .map(it => ({ itemId: Number(it.itemId), quantity: Number(it.quantity) || 0 })),
        };
        let longRes;
        if (longRecord.id) {
          longRes = await APIEndpoint.put(`catering-group/config/${configId}/standard-uplift`, longPayload, hdrs);
        } else {
          longRes = await APIEndpoint.post(`catering-group/config/${configId}/standard-uplift`, longPayload, hdrs);
        }
        if (longRes?.data?.id) setLongRecord(prev => ({ ...prev, id: longRes.data.id }));
            toastSuccess(longRes?.message || 'Standard uplift saved successfully.');
      }

      toastSuccess(defRes?.message || 'Standard uplift saved successfully.');
     
      if (activate) {
        // let galleyError = false;
        if (galleySaveRef.current) {
          try {
            await galleySaveRef.current();
          } catch (e) {
            // galleyError = true;
            console.error('galley save error:', e);
            const eg = JSON.parse(e.message);
            toastError(eg?.data?.message || 'Uplift saved but failed to save galley configuration.');
          }
        }
        // if (!galleyError) onBack();
      }
    } catch (err) {
      console.error('saveStdUplift error:', err);
      const errorObj = JSON.parse(err.message);
      toastError(errorObj?.data?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function addDefaultItem() {
    setDefaultRecord(prev => ({ ...prev, items: [...prev.items, { _key:Date.now(), itemId:null, quantity:0 }] }));
  }
  function updateDefaultItem(_key, field, val) {
    setDefaultRecord(prev => ({ ...prev, items: prev.items.map(it => it._key === _key ? { ...it, [field]: val } : it) }));
  }
  function removeDefaultItem(_key) {
    setDefaultRecord(prev => ({ ...prev, items: prev.items.filter(it => it._key !== _key) }));
  }

  
  function updateLongItem(_key, val) {
    setLongRecord(prev => ({ ...prev, items: prev.items.map(it => it._key === _key ? { ...it, quantity: val } : it) }));
  }

  function handleLongHaulToggle() {
    const next = !longHaulEnabled;
    setLongHaulEnabled(next);
    if (next && longRecord.items.length === 0 && defaultRecord.items.length > 0) {
      setLongRecord(prev => ({
        ...prev,
        items: defaultRecord.items.map(it => ({ _key: Date.now() + Math.random(), itemId: it.itemId, quantity: it.quantity })),
      }));
    }
  }

  const masterOptions = masterItems.map(m => ({ value: m.itemId, label: m.itemName }));

  function getItemName(itemId) {
    return masterItems.find(m => m.itemId === Number(itemId))?.itemName || `Item ${itemId}`;
  }


  function DiffBadge({ defaultQty, longQty }) {
    const diff = Number(longQty) - Number(defaultQty);
    if (!diff) return <span style={{ color:"#3d5a7a" }}>—</span>;
    return (
      <span style={{
        display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:12, fontWeight:600,
        background: diff > 0 ? "rgba(76,174,76,0.15)" : "rgba(224,92,92,0.15)",
        color: diff > 0 ? "#4cae4c" : "#e05c5c",
      }}>
        {diff > 0 ? `+${diff}` : diff}
      </span>
    );
  }

  const pageTabs = [
    {id:"items",  label:"Items & Uplift", icon:(<svg width="14" height="14" viewBox="0 -960 960 960" fill="currentColor" style={{opacity:0.7}} ><path d="M280-600q-33 0-56.5-23.5T200-680q0-33 23.5-56.5T280-760q33 0 56.5 23.5T360-680q0 33-23.5 56.5T280-600Zm0 320q-33 0-56.5-23.5T200-360q0-33 23.5-56.5T280-440q33 0 56.5 23.5T360-360q0 33-23.5 56.5T280-280ZM440-640h320v-80H440v80Zm0 320h320v-80H440v80Z"></path></svg>)},
    {id:"galley", label:"Galley & Trolley", icon: (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{opacity:0.7}} ><path d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"></path></svg>)},
    {id:"config", label:"Uplift Config", icon: (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{opacity:0.7}} ><path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.92c.04-.34.07-.68.07-1.08s-.03-.73-.07-1.08l2.3-1.82c.21-.16.27-.46.13-.7l-2.18-3.78c-.14-.24-.44-.32-.68-.24l-2.7 1.1c-.57-.44-1.18-.81-1.85-1.09l-.4-2.89C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.4 2.89c-.67.28-1.28.65-1.85 1.09l-2.7-1.1c-.24-.09-.54 0-.68.24L1.7 9.32c-.14.24-.08.54.13.7l2.3 1.82C4.03 12.27 4 12.61 4 13s.03.73.07 1.08l-2.3 1.82c-.21.16-.27.46-.13.7l2.18 3.78c.14.24.44.32.68.24l2.7-1.1c.57.44 1.18.81 1.85 1.09l.4 2.89c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.4-2.89c.67-.28 1.28-.65 1.85-1.09l2.7 1.1c.24.09.54 0 .68-.24l2.18-3.78c.14-.24.08-.54-.13-.7l-2.3-1.82z"></path></svg>)},
  ];

 
  async function downloadTemplate(haulType) {
    try {
      const hdrs = await getHeaders();
      if (!hdrs) return;
      const header = {
        ...APIEndpoint.getHeaders().headers,
        user_id: hdrs.user_id,
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      const res = await fetch(
        APIEndpoint.getFullPath(`catering-group/config/${configId}/standard-uplift/template?haulType=${haulType}`),
        { method: 'GET', responseType: 'arrayBuffer', headers: header }
      );
      const blob = await res.blob();
      saveAs(blob, `standard_uplift_${haulType}_template.xlsx`);
    } catch (err) {
      console.error('downloadTemplate error:', err);
      toastError('Failed to download template.');
    }
  }

  function importCSV(haulType, onImport) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx,.xls";
    input.onchange = async e => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const hdrs = await getHeaders();
        if (!hdrs) return;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("haulType", haulType);
        const header = {
          ...APIEndpoint.getHeaders().headers,
          user_id: hdrs.user_id,
        };
        const res = await APIEndpoint.post(
          `catering-group/config/${configId}/standard-uplift/upload`,
          formData,
          header
        );
        if (res?.success) {
          const imported = (res.data?.items || []).map((it, i) => ({
            _key: Date.now() + i,
            itemId: it.itemId,
            quantity: it.quantity,
          }));
          if (imported.length) {
            onImport(imported);
            toastSuccess(res.message || `Imported ${imported.length} item(s).`);
          } else {
            toastError("No valid rows found in file.");
          }
        } else {
          if (res?.errors?.length) {
            setImportError({ open:true, errors: res.errors, message: res.message });
          } else {
            toastError(res?.message || 'Import failed.');
          }
        }
      } catch (err) {
        console.error('importCSV error:', err);
        const eEr = JSON.parse(err.message);
        setImportError({ open:true, errors: eEr?.data?.errors || [], message: eEr?.data?.message || 'Import failed.' });
      }
    };
    input.click();
  }
 
  const toolbarBtnBase = { display:"flex", alignItems:"center", gap:6, padding:"6px 14px", fontSize:13 };
  const DownloadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
  const UploadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark fade-in page-wrap">
      <ImportErrorModal
        open={importError.open}
        errors={importError.errors}
        message={importError.message}
        onClose={() => setImportError({ open:false, errors:[], message:"" })}
      />
      <button className="back-btn" onClick={onBack}>← Back to Configurations</button>

      <div className="mb-24">
        <h1 className="wizard-page-title">Standard Uplift</h1>
        <p className="wizard-page-sub mb-14">
          Configure item quantities and long haul rules for each aircraft configuration. These will drive automatic uplift planning in DFP Calendar.
        </p>
        <div className="flex gap-8 items-center flex-wrap">
          {config?.aircraft?.length > 0 && config.aircraft.map(a => <AcChip key={a.code} code={a.code} ac={a} />)}
          <OpsBadge ops={config?.ops || "both"} label={config?.opsLabel || "Catering + Duty Free"} />
          <StatusBadge status={config?.status || "draft"} />
          {config?.updated && config.updated !== "—" && (
            <span style={{ fontSize:12.5, color:"#3d5a7a", marginLeft:4 }}>Last updated: {config.updated}</span>
          )}
        </div>
      </div>


      <div className="tab-bar">
        {pageTabs.map(tab => (
          <button key={tab.id} className={`flexRow justifyContentCenter alignItemsCenter tab-btn ${activeTab===tab.id?"tab-btn--active":""}`} onClick={() => setActiveTab(tab.id)}>
            {tab.icon} &nbsp;
            {tab.label}</button>
        ))}
      </div>

      {activeTab==="items" && (
        <div className="panel mb-20">
          {loading ? (
            <div style={{ padding:"40px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>Loading items…</div>
          ) : (
            <>
              <div className="panel-body">
                <SectionDivider label="Items" />
               
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, gap:12 }}>
                  <p className='toggle-desc' style={{ margin:0 }}>
                    Add all items and their standard quantities per catering group.
                  </p>
                  <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                    <button className="btn btn-ghost" style={toolbarBtnBase} onClick={() => downloadTemplate("default")}>
                      <DownloadIcon /> Download Template
                    </button>
                    <button className="btn btn-primary-download" style={toolbarBtnBase}
                      onClick={() => importCSV("default", imported => setDefaultRecord(prev => ({ ...prev, items: [...prev.items, ...imported] })))}>
                      <UploadIcon /> Import CSV / XLSX
                    </button>
                  </div>
                </div>
                <div style={{ maxHeight:280, overflowY:"auto", border:"1px solid rgba(40,80,140,0.3)", borderRadius:8, marginBottom:8 }}>
                <table className="data-table mb-8 catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
                  <colgroup>
                    <col style={{ width:"60%" }} />
                    <col style={{ width:"30%" }} />
                    <col style={{ width:"10%" }} />
                  </colgroup>
                  <thead>
                    <tr>{["Item / Category","Max Qty in Trolley",""].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {defaultRecord.items.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ padding:"24px 0", textAlign:"center", color:"#3d5a7a", fontSize:13 }}>
                          No uplift items yet. Click "+ Add item" below to start.
                        </td>
                      </tr>
                    ) : defaultRecord.items.map(item => {
                      const rowOptions = masterOptions.filter(
                        opt => opt.value === item.itemId || !defaultRecord.items.some(it => it._key !== item._key && it.itemId === opt.value)
                      );
                      const selectedOption = masterOptions.find(opt => opt.value === item.itemId) || null;
                      return (
                        <tr key={item._key}>
                          <td style={{ overflow:"visible" }}>
                            <CustomSelect
                              placeHolder="Select item"
                              controlStyle={{ border:"1px solid rgba(40,80,140,0.45)", borderRadius:7 }}
                              options={rowOptions}
                              value={selectedOption}
                              onChange={opt => updateDefaultItem(item._key, "itemId", opt ? opt.value : null)}
                              menuPortalTarget={null}
                              menuPosition="absolute"
                              isClearable={true}
                              isSearchable={true}
                              width="100%"
                              autoWidth={true}
                            />
                          </td>
                          <td>
                            <input
                              type="text" inputMode="numeric" pattern="[0-9]*"
                              style={{ width:"100%" }}
                              value={item.quantity ?? ""}
                              onChange={e => updateDefaultItem(item._key, "quantity", e.target.value.replace(/[^0-9]/g,""))}
                            />
                          </td>
                          <td>
                            <button
                              onClick={() => removeDefaultItem(item._key)}
                              style={{ padding:"4px 8px", border:"none", background:"none", color:" #e05c5c", fontSize:16, cursor:"pointer", lineHeight:1 }}
                              title="Remove item"
                            >×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <button className="add-row-btn mb-20" onClick={addDefaultItem}>
                  <span className="add-row-btn__icon">+</span> Add item
                </button>

                <SectionDivider label="Long Haul Configuration" />
                <Notice>Long haul flights may require different uplift quantities. Define the duration threshold and alternate quantities here.</Notice>

                <Toggle
                  label="Enable separate uplift quantities for long haul flights"
                  desc="Configure a different standard uplift plan when a flight exceeds the long haul threshold"
                  on={longHaulEnabled}
                  onToggle={handleLongHaulToggle}
                />

                {longHaulEnabled && (
                  <div className="mt-16">
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#8aaacf", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>
                        Long Haul Duration Threshold <span style={{ color:" #e05c5c" }}>*</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <input
                          type="text" inputMode="numeric" pattern="[0-9]*"
                          value={longHaulHours ?? ""}
                          onChange={e => setLongHaulHours(e.target.value.replace(/[^0-9]/g,""))}
                          style={{ width:80 }}
                        />
                        <span style={{ fontSize:13, color:"#3d5a7a" }}>hours</span>
                      </div>
                      <div style={{ fontSize:12, color:"#3d5a7a", marginTop:6 }}>
                        Flights at or above this duration use long haul quantities
                      </div>
                    </div>

         
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, gap:12 }}>
                      <p className="toggle-desc" style={{ margin:0 }}>
                        Long haul quantities override standard quantities for flights exceeding the threshold.
                      </p>
                      <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                        <button className="btn btn-ghost" style={toolbarBtnBase} disabled={defaultRecord.items.filter(it => it.itemId).length === 0} onClick={() => downloadTemplate("long")}>
                          <DownloadIcon /> Download Template
                        </button>
                        <button className="btn btn-primary-download" style={toolbarBtnBase}
                          disabled={defaultRecord.items.filter(it => it.itemId).length === 0}
                          onClick={() => importCSV("long", imported => setLongRecord(prev => ({ ...prev, items: [...prev.items, ...imported] })))}>
                          <UploadIcon /> Import CSV / XLSX
                        </button>
                      </div>
                    </div>
                    {defaultRecord.items.filter(it => it.itemId).length === 0 ? (
                      <Notice warn icon="⚠">Add items in the standard section above first.</Notice>
                    ) : (
                    <div style={{ maxHeight:280, overflowY:"auto", border:"1px solid rgba(40,80,140,0.3)", borderRadius:8, marginBottom:8 }}>
                      <table className="data-table mb-8 catering-table-main" style={{ tableLayout:"fixed", width:"100%" }}>
                        <colgroup>
                          <col style={{ width:"30%" }} />
                          <col style={{ width:"22%" }} />
                          <col style={{ width:"25%" }} />
                          <col style={{ width:"23%" }} />
                        </colgroup>
                        <thead>
                          <tr>{["Item","Standard Qty (Short Haul)","Long Haul Qty","Difference"].map(h => <th key={h}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {defaultRecord.items.filter(it => it.itemId).map(defItem => {
                            const longItem = longRecord.items.find(li => li.itemId === defItem.itemId);
                            const longQty  = longItem?.quantity ?? defItem.quantity;
                            const longKey  = longItem?._key;
                            return (
                              <tr key={defItem._key}>
                                <td style={{ color:"#c8daea" }}>{getItemName(defItem.itemId)}</td>
                                <td style={{ color:"#3d5a7a" }}>{defItem.quantity}</td>
                                <td>
                                  <input
                                    type="text" inputMode="numeric" pattern="[0-9]*"
                                    style={{ width:"100%" }}
                                    value={longQty ?? ""}
                                    onChange={e => {
                                      if (longKey) {
                                        updateLongItem(longKey, e.target.value);
                                      } else {
                                        setLongRecord(prev => ({
                                          ...prev,
                                          items: [...prev.items, { _key:Date.now()+Math.random(), itemId:defItem.itemId, quantity:+e.target.value }],
                                        }));
                                      }
                                    }}
                                  />
                                </td>
                                <td><DiffBadge defaultQty={defItem.quantity} longQty={longQty} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab==="galley" && (
        <GalleyTrolleyTab config={config} getHeaders={getHeaders} onRegisterSave={fn => { galleySaveRef.current = fn; }} />
      )}

      {activeTab==="config" && (
        <div className="panel mb-20">
          <div className="panel-header">
            <div className="panel-icon">⚙️</div>
            <div><div className="panel-title">Uplift Configuration Settings</div><div className="panel-subtitle">Pre-book thresholds and uplift schedule for this configuration</div></div>
          </div>
          <div className="panel-body">
            <SectionDivider label="Pre-order & Threshold Settings"/>
            <div className="form-grid-2">
              <FormGroup label="Pre-book threshold (no. of passengers)" required hint="Minimum passengers required to trigger catering group re-evaluation.">
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={prebookThreshold} className="max-w-160" onChange={e => setPrebookThreshold(e.target.value.replace(/[^0-9]/g, ""))}/>
              </FormGroup>
              <FormGroup label="Action when threshold is breached">
                <select value={thresholdBreachAction} onChange={e => setThresholdBreachAction(e.target.value)}>
                  <option value="ALERT">Alert catering lead — manual re-group decision</option>
                  <option value="AUTO_REGROUP">Auto create new catering group</option>
                </select>
              </FormGroup>
            </div>
            <SectionDivider label="Sales Commit Threshold"/>
            <Toggle label="Enable Sales Commit Threshold" desc="Define the maximum variance between uplifted stock and recorded sales before a flight can be committed" on={salesCommitEnabled} onToggle={() => setSalesCommitEnabled(v => !v)}/>
            {salesCommitEnabled && (
              <div className="mt-16">
                <FormGroup label="Allowed Discrepancy Threshold" required>
                  <div className="range-row">
                    <input type="range" max={20} step={0.5} value={commitThresh} onChange={e=>setCommitThresh(+e.target.value)}/>
                    <span className="range-val">{commitThresh.toFixed(1)}%</span>
                  </div>
                </FormGroup>
              </div>
            )}
            <SectionDivider label="Activation & Expiry Schedule"/>
            <div className="form-grid-2">
              <FormGroup label="Activation Date" required hint="This uplift configuration becomes active from this date."><input type="date" value={activationDate} onChange={e => setActivationDate(e.target.value)}/></FormGroup>
              <FormGroup label="Expiry Date" hint="Leave blank if this configuration has no expiry."><input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}/></FormGroup>
            </div>
          </div>
        </div>
      )}

      <div className="page-footer">
        <Btn variant="ghost" onClick={onBack}>Cancel</Btn>
        <button className="btn-draft" disabled={saving || loading} onClick={() => handleSave(false)}>
          {saving ? "Saving…" : "Save as Draft"}
        </button>
        <Btn variant="success" disabled={loading} onClick={() => handleSave(true)}>
          {saving ? "Saving…" : "Save & Activate"}
        </Btn>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
    — Catering screen
───────────────────────────────────────────────────────────────────────────── */
export const Catering = () => {
  const [view, setView]                   = useState("listing");
  const [viewingConfig, setViewingConfig] = useState(null);
  const [stdUpliftConfig, setStdUpliftConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [prevView, setPrevView]           = useState("listing");
  const [wizardKey, setWizardKey]         = useState(0);
  const [editKey, setEditKey]             = useState(0);
  const configs = [];

  const [newConfigId, setNewConfigId] = useState(null);

const handleSaveConfig = (savedData, configId) => {
    if (configId && !editingConfig) setNewConfigId(configId);
  };
  const handleStdUpliftBack = () => { setNewConfigId(null); setView("listing"); };

  const openWizardForNew = () => { setEditingConfig(null); setPrevView("listing"); setWizardKey(k=>k+1); setView("wizard"); };
  const openEditPage     = row => { const from=view; setEditingConfig(row); setPrevView(from); setEditKey(k=>k+1); setView("edit"); };
  const handleWizardBack = () => { setEditingConfig(null); setView(prevView); };
  const handleEditBack   = () => { setEditingConfig(null); setView(prevView); };

  return (
    <div className="catering-shell">
      <ToastContainer style={{ zIndex: 999999 }} />
      <div className="catering-main">
        {view==="listing" && (
          <ListingPage configs={configs} newConfigId={newConfigId} onNewConfig={openWizardForNew}
            onView={row => { setViewingConfig(row); setView("view"); }}
            onEdit={openEditPage}
            onStdUplift={row => { setStdUpliftConfig(row || null); setView("stdUplift"); }} />
        )}
        {view==="wizard" && (
          <WizardPage key={wizardKey} editData={null} onBack={handleWizardBack} onSaveConfig={handleSaveConfig}
             onStdUplift={(id, wizardData) => {
              setStdUpliftConfig({
                id,
                aircraft: (wizardData?.aircraftTypesList || []).filter(a => (wizardData?.selectedAircraft || []).includes(a.id)).map(a => ({ id: a.id, code: a.code, body: a.body || "—" })),
                _aircraftTypeIds: wizardData?.selectedAircraft || [],
                ops: wizardData?.opsType || "both",
                opsLabel: wizardData?.opsType === "cateringOnly" ? "Catering Only" : wizardData?.opsType === "dutyFreeOnly" ? "Duty Free Only" : "Catering + Duty Free",
                status: "active",
              });
              setView("stdUplift");
            }} />
        )}

        {view==="edit" && editingConfig && (
          <WizardPage key={editKey} editData={editingConfig} onBack={handleEditBack} onSaveConfig={handleSaveConfig}
            onStdUplift={() => { setStdUpliftConfig(editingConfig); setView("stdUplift"); }} />
        )}
        {view==="view" && viewingConfig && (
          <ViewPage config={viewingConfig} onBack={() => setView("listing")} onEdit={openEditPage}
            onStdUplift={() => { setStdUpliftConfig(viewingConfig); setView("stdUplift"); }} />
        )}
         {view==="stdUplift" && (
          <StdUpliftPage config={stdUpliftConfig} onBack={handleStdUpliftBack} closeOnSave={false} />
        )}
      </div>
    </div>
  );
};